import { MF } from "../components/NumberInputs";
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import api from '../api/client';
import FinTable from "../components/FinTable";
import theme from '../components/theme';
import { Card, PageHeader, StatCard, Badge, Button, Tabs } from '../components/ui';
import Modal from '../components/Modal';
import PostedLedgerEntries from '../components/PostedLedgerEntries';
import BankBalancesPanel from '../components/BankBalancesPanel';
import { ExportCsvButton } from '../components/PageTools';
import {
  MAX_PER_TURN, modelLabel, pictureFiles, preparePicture, recentPictures, rememberPictures,
} from '../components/pictures';

/* ───────────────────────── palette ─────────────────────────────
   Aliases onto the shared theme (components/theme.js), kept under the old
   names so the two thousand lines of modal below need no rewrite. This page
   used to carry its own dark fintech palette — saturated blue, purple, cyan
   and orange — which a runtime remapper turned light without touching the
   accents, so it sat beside Ledger and Deals looking like a different
   product. The old blue, purple, cyan and orange all collapse onto the
   theme's few semantic colours, and that is the point. */
const C = {
  bg: theme.bg, card: theme.bgCard, border: theme.border, text: theme.text,
  muted: theme.textMuted, accent: theme.accent, green: theme.success,
  yellow: theme.warning, red: theme.danger, orange: theme.warning,
  purple: theme.info, cyan: theme.info, inputBg: theme.bgInput,
  rowHover: theme.tableRowHover,
};

/* The suggester's four confidences, in the words the Review card uses. The
   grid's Suggested column and its filter read the same table, so a row
   filtered as "Confident" opens onto a card that says "Confident". */
const CERTAINTY = { high: 'Confident', medium: 'Likely', low: 'Needs splitting', none: 'Cannot tell' };
const CERTAINTY_VARIANT = { high: 'green', medium: 'amber', low: 'yellow', none: 'gray' };
const CERTAINTY_ORDER = ['high', 'medium', 'low', 'none'];

/* A figure and the currency it is actually in. A euro wire rendered with a
   dollar sign is read as dollars by everyone who sees it afterwards, so
   anything showing a bank row's own amount goes through here with the row's
   currency rather than through fmt(). */
const fmtCcy = (v, ccy) => {
  if (v == null || v === '') return '—';
  const n = parseFloat(v);
  if (isNaN(n)) return '—';
  const abs = Math.abs(n).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  const c = (ccy || 'USD').toUpperCase();
  if (c === 'USD') return n < 0 ? `-$${abs}` : `$${abs}`;
  return `${n < 0 ? '-' : ''}${abs} ${c}`;
};

const fmt = (v) => fmtCcy(v, 'USD');

/* Rate a confirmed dollar figure implies, which is generally not the rate the
   bank quoted — that difference is the reason the two are kept apart. */
const impliedRate = (usd, amount) => {
  const u = Math.abs(parseFloat(usd));
  const a = Math.abs(parseFloat(amount));
  if (!u || !a || isNaN(u) || isNaN(a)) return null;
  return (u / a).toFixed(6).replace(/0+$/, '').replace(/\.$/, '');
};

/* The figure the allocations are stated in, and what to call it. Mirrors
   BankFeedStaging.posting_amount / posting_currency on the server — the two
   have to agree or the balance check fails on figures the screen said were
   right. Three tiers, best first: the dollars a reviewer took off the wire
   advice, the bank's own conversion, and the row's own currency when there is
   no rate at all. */
const has = (v) => v !== null && v !== undefined && v !== '';

const postingBasis = (txn) => {
  const rowCcy = (txn.currency || 'USD').toUpperCase();
  if (has(txn.usd_amount_override)) {
    return {
      source: 'reviewer', adjusted: true, currency: 'USD',
      amount: Math.abs(parseFloat(txn.usd_amount_override)) || 0,
    };
  }
  // A foreign row with nothing confirmed has no posting basis. The figure
  // shown is the row's own, for display; it never reaches the ledger,
  // because `needs_usd_amount` stops the row first.
  return {
    source: 'row', adjusted: false, currency: rowCcy,
    amount: Math.abs(parseFloat(txn.amount)) || 0,
  };
};

const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' }); }
  catch { return v; }
};

/* ── GL account options ──
   The chart of accounts comes from the server —
   `GET /api/v1/ledger/entries/chart-of-accounts/`, which serves
   `ledger/chart_of_accounts.py`. It used to be a twelve-item array written
   here, and twelve accounts is a shortlist, not a chart: there was no account
   at all for office space, so the WeWork bills had nowhere to go, and the fund
   administrator's invoices could only be filed under something labelled
   "Admin". Whatever has no account ends up in Expense:Operating, and a P&L
   whose biggest line is "Operating" answers nothing.

   Keeping it on the server means adding an account is a one-file Python change
   that reaches this picker without a frontend build — which matters, because
   `frontend/build/` is committed and shipped from git rather than built in CI.

   FALLBACK below is what renders if that call fails. It is deliberately short:
   enough to keep the screen usable, not a second copy of the chart to drift
   out of step with the first. */
const FALLBACK_CHART = [
  { type: 'asset', label: 'Assets — things we own', groups: [
    { group: 'Cash', items: [
      { value: 'Assets:Cash', label: 'Broker cash', hint: 'The fund’s cash at its brokers: sales add to it on their deal; sweeps and conversions move it with no deal. Never this row’s own bank.' },
    ]},
    { group: 'Investments', items: [
      { value: 'Assets:Investments:Notes', label: 'Investments — Notes', hint: 'Convertible and other notes we hold, at what we paid.' },
      { value: 'Assets:Investments:Preferred', label: 'Investments — Preferred', hint: 'Preferred shares we hold, at what we paid.' },
      { value: 'Assets:Investments:Stock', label: 'Investments — Stock', hint: 'Common shares we hold, at what we paid.' },
      { value: 'Assets:Investments:Warrants', label: 'Investments — Warrants', hint: 'Warrants we hold, at the cost allocated to them.' },
      { value: 'Assets:Investments:ELOC', label: 'Investments — ELOC', hint: 'Equity line positions.' },
    ]},
  ]},
  { type: 'income', label: 'Income — money we earned', groups: [
    { group: 'Investment income', items: [
      { value: 'Income:InterestIncome', label: 'Interest income', hint: 'Interest, and early-redemption premiums.' },
      { value: 'Income:DividendIncome', label: 'Dividend income', hint: 'Dividends received.' },
      { value: 'Income:Realized Gains', label: 'Realized gain', hint: 'Profit above what we paid.' },
    ]},
  ]},
  { type: 'expense', label: 'Expenses — money we spent', groups: [
    { group: 'Operating', items: [
      { value: 'Expense:Legal', label: 'Legal fees', hint: 'Law firm invoices.' },
      { value: 'Expense:Admin', label: 'Fund administration', hint: 'IQ-EQ, Apex, Citco, the transfer agent.' },
      { value: 'Expense:Rent', label: 'Rent and office space', hint: 'WeWork, Regus, office rent.' },
      { value: 'Expense:Operating', label: 'Other operating', hint: 'Only when nothing more specific fits.' },
    ]},
  ]},
];

/* Flatten a chart into { value: {label, hint, group, section} } so a stored
   account string can be described without walking the tree each render. */
const flattenChart = (sections) => {
  const out = {};
  (sections || []).forEach(sec =>
    (sec.groups || []).forEach(g =>
      (g.items || []).forEach(i => {
        out[i.value] = { ...i, group: g.group, section: sec.label };
      })
    )
  );
  return out;
};

/* One `<option>`, showing how often the book has used the account where the
   server told us. The count is the most useful thing on the line: an account
   with 725 entries beside one with 1 says which spelling is the book's and
   which is a typo, without any warning text. */
const chartOption = (i) => (
  <option key={i.value} value={i.value}
    title={i.hint ? `${i.label} — ${i.hint}` : i.label}>
    {i.label}{i.entries ? ` (${i.entries})` : ''}
  </option>
);

function GLSelect({ value, onChange, style, chart, byValue, inUse, cashAccount, cashInfo }) {
  const say = (a) => { const d = describeCash(a, cashInfo); return d ? `${d} — ${a}` : a; };
  const sections = (chart && chart.length) ? chart : FALLBACK_CHART;
  const lookup = byValue || flattenChart(sections);
  const chosen = lookup[value];
  // The bank account this row's money moved through. It is the answer on
  // nearly every bank line, and it is not in the chart because it is per-row —
  // so it goes at the top rather than being hunted for among the accounts
  // already used in the book.
  const ownCash = cashAccount && cashAccount !== 'Assets:Cash' ? cashAccount : '';
  // Accounts the book posts to that the chart does not name. Per-owner
  // accounts (Distributions to Owners:<name>, Due from <name>) can never be
  // listed centrally — a new owner means a new account — and until these were
  // merged in they could not be picked at all, which is why every distribution
  // paid out of the bank had nowhere correct to go.
  const extra = (inUse || [])
    .filter(a => !lookup[a.account] && a.account !== ownCash)
    .sort((a, b) => b.entries - a.entries);
  // A value already on the line but in neither list still has to be selectable,
  // or opening a saved allocation would silently blank it.
  const orphan = value && !lookup[value] && value !== ownCash
    && !extra.some(a => a.account === value);
  return (
    <select value={value} onChange={onChange} style={style}
      title={chosen ? `${chosen.label} — ${chosen.hint}` : 'Pick the account for this side of the entry'}>
      <option value="">— Select —</option>
      {orphan && <option value={value}>{say(value)}</option>}
      {ownCash && (
        <optgroup label="This row's bank account">
          <option value={ownCash} title="Cash in the account this transaction actually moved through.">
            {say(ownCash)}
          </option>
        </optgroup>
      )}
      {/* flatMap, not a nested map: an array of arrays makes React ask for a
          key on each inner array, and the optgroups are what carry keys. */}
      {sections.flatMap(sec =>
        (sec.groups || []).map(g => (
          <optgroup key={`${sec.type}:${g.group}`} label={`${sec.label.split(' — ')[0]} · ${g.group}`}>
            {(g.items || []).map(chartOption)}
          </optgroup>
        ))
      )}
      {extra.length > 0 && (
        <optgroup label="Also used in your book">
          {extra.map(a => (
            <option key={a.account} value={a.account}
              title={`${a.entries} entr${a.entries === 1 ? 'y' : 'ies'}, last used ${a.last_used || 'unknown'}`}>
              {say(a.account)} ({a.entries})
            </option>
          ))}
        </optgroup>
      )}
    </select>
  );
}

/* ── Which bank a row came from ──
   The staging table began as Mercury-only and its columns still carry Mercury
   names; `source` is what actually distinguishes the banks now that Citi rows
   share the table. A row with no source is a Mercury row from before the
   column existed. */
const sourceName  = (src) => ({ citi: 'Citi', mercury: 'Mercury', manual: 'Manual' }[src] || 'Mercury');
const sourceColor = (src) => (src === 'citi' ? theme.info : src === 'manual' ? theme.textMuted : theme.accent);

/* Which bank a row came from, as a dot in that bank's colour — Citi in the
   theme's blue, Mercury in the house amber — so the table can be scanned by
   bank without a word of text per row. */
function BankDot({ source }) {
  return (
    <span title={sourceName(source)} style={{
      display: 'inline-block', width: 7, height: 7, borderRadius: '50%', flexShrink: 0,
      background: sourceColor(source), marginRight: 7, verticalAlign: 'middle',
    }} />
  );
}

/* What to call the account a row moved through. The server sends the bank's
   own nickname where one is set (HGIO1, HILBB) and the feed's name otherwise;
   "Mercury Checking ••9567" loses its "Mercury", because the dot already says
   that. The entity's short name goes first: whose money it is matters more
   than which of their accounts. */
const accountLabel = (txn, accountMap) => {
  const raw = txn.bank_account_label || txn.mercury_account_name ||
              (accountMap && accountMap[txn.mercury_account_id]) ||
              (txn.mercury_account_id || '').substring(0, 8);
  const short = (txn.source || 'mercury') === 'mercury' ? raw.replace(/^Mercury\s*-?\s*/i, '') : raw;
  return { raw, short, entity: txn.entity_short_name || '' };
};

/* A GL account name has no spaces until its last segment
   ("Liabilities:DistributionsPayable:Employee 0"), so a narrow cell could not
   break it at all and the whole name set the column's width. It may break
   after a colon or between the words of a run-together segment
   ("Distributions|Payable"), which keeps every piece readable. */
const breakableAccount = (name) => String(name)
  .replace(/:/g, ':\u0000').replace(/([a-z])([A-Z])/g, '$1\u0000$2')
  .split('\u0000')
  .map((part, i) => <React.Fragment key={i}>{i > 0 && <wbr />}{part}</React.Fragment>);

/* ── Transfer detection helpers (client-side fallback) ── */
const MERCURY_PREFIXES = ['Mercury - Checking', 'Mercury - Savings'];
const ENTITY_PREFIXES  = ['Helena Partners', 'Helena Global Investment Opportunities'];

function classifyTransfersClientSide(txns) {
  /* Mutates in place: adds _transferType and _matchedId fields */
  const matched = new Set();

  // Find outgoing transfers: kind=externalTransfer + counterparty starts with Mercury
  const outgoing = txns.filter(t => {
    if (t.transfer_type) return false; // already classified server-side
    const cp = (t.counterparty_name || '').trim();
    return t.kind === 'externalTransfer' && MERCURY_PREFIXES.some(p => cp.startsWith(p));
  });

  outgoing.forEach(out => {
    const outAmt = Math.abs(parseFloat(out.amount));
    const outDate = out.posted_date || out.created_date;
    if (!outDate || outAmt === 0) return;

    // Find matching incoming on a different account
    const match = txns.find(t => {
      if (t.mercury_txn_id === out.mercury_txn_id) return false;
      if (matched.has(t.mercury_txn_id)) return false;
      if (t.mercury_account_id === out.mercury_account_id) return false;
      const amt = parseFloat(t.amount);
      if (amt <= 0) return false;
      if (Math.abs(amt - outAmt) > 0.01) return false;
      const tDate = t.posted_date || t.created_date;
      if (!tDate) return false;
      const diff = Math.abs(new Date(outDate) - new Date(tDate));
      return diff <= 2 * 86400000; // ±2 days
    });

    if (match) {
      matched.add(out.mercury_txn_id);
      matched.add(match.mercury_txn_id);
      out._transferType = out.transfer_type || 'internal_out';
      out._matchedId = match.mercury_txn_id;
      match._transferType = match.transfer_type || 'internal_in';
      match._matchedId = out.mercury_txn_id;
    }
  });

  // Mark remaining as external (client-side)
  txns.forEach(t => {
    if (!t._transferType) {
      t._transferType = t.transfer_type || 'external';
    }
  });

  return txns;
}


/* ═══════════════════════ BANK FEED PAGE ═══════════════════════ */
export default function BankFeed() {
  const [txns, setTxns]             = useState([]);
  const [filter, setFilter]         = useState('unposted');
  const [loading, setLoading]       = useState(true);
  const [summary, setSummary]       = useState({});
  // Has anything been booked off a payment that never moved? (§0.19)
  const [unsettledAudit, setUnsettledAudit] = useState(null);
  useEffect(() => {
    if (filter !== 'failed') return;
    setUnsettledAudit(null);
    api.get('/reconciliation/bank-feed/unsettled-postings/')
      .then(r => setUnsettledAudit(r.data))
      .catch(() => setUnsettledAudit({ error: true }));
  }, [filter]);
  const [deals, setDeals]           = useState([]);
  const [entities, setEntities]     = useState([]);
  const [selected, setSelected]     = useState(new Set());
  const [editTxn, setEditTxn]       = useState(null);
  const [showCitiImport, setShowCitiImport] = useState(false);
  const [accountsInUse, setAccountsInUse] = useState([]);
  const [chart, setChart] = useState([]);
  const [posting, setPosting]       = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // ── NEW: account + transfer filters ──
  const [accountFilter, setAccountFilter] = useState('all');
  const [transferFilter, setTransferFilter] = useState('all');
  // The suggester's confidence on each open row: 'all', or one of
  // CERTAINTY_ORDER. Set to 'high', select all, Apply suggestions, Post to GL.
  const [suggestionFilter, setSuggestionFilter] = useState('all');
  const [applying, setApplying]       = useState(false);
  const [showBookAll, setShowBookAll] = useState(false);  // Book all as… modal
  const [accountMap, setAccountMap]   = useState({});  // id → name
  const [transferSummary, setTransferSummary] = useState(null);
  const [detecting, setDetecting]     = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [syncing, setSyncing]       = useState(false);
  const PAGE_SIZE = 100;

  /* ── load Mercury account names ──────────────────────── */
  const [mercuryAccounts, setMercuryAccounts] = useState([]);  // every account, with its connection's entity
  const loadAccountMap = useCallback(async () => {
    try {
      const res = await api.get('/integrations/mercury/accounts/');
      const map = {};
      const all = [];
      (res.data.connections || []).forEach(conn => {
        (conn.accounts || []).forEach(a => {
          map[a.id] = a.name;
          all.push({ ...a, entityLabel: conn.label || '' });
        });
      });
      setAccountMap(map);
      setMercuryAccounts(all);
    } catch { /* Mercury not connected — that's OK */ }
  }, []);

  // GL cash account string -> { entity, name, nickname, kind }, so any screen
  // can say whose account "Mercury Checking ••6816" is. The connection is
  // labelled with the entity's full name; the entities list turns that into
  // the short name used everywhere else (HPI, HGIO).
  const cashInfo = useMemo(() => {
    const shortByName = {};
    entities.forEach(e => { shortByName[(e.name || '').trim().toLowerCase()] = e.short_name || e.name; });
    const out = {};
    mercuryAccounts.forEach(a => {
      if (!a.name) return;
      const key = (a.entityLabel || '').trim().toLowerCase().replace(/\.$/, '');
      const entity = shortByName[key] || shortByName[key + '.'] || a.entityLabel || 'Mercury';
      out[`Assets:Cash:Mercury:${a.name}`] = {
        entity, name: a.name, nickname: a.nickname || '', kind: a.kind || a.type || '',
      };
    });
    return out;
  }, [mercuryAccounts, entities]);

  /* ── load transfer summary (tries backend, falls back) ─ */
  const loadTransferSummary = useCallback(async () => {
    try {
      const res = await api.get('/reconciliation/bank-feed/transfer-summary/');
      setTransferSummary(res.data);
    } catch {
      // Backend endpoint not deployed yet — compute client-side below
      setTransferSummary(null);
    }
  }, []);

  /* ── load data ───────────────────────────────────────── */
  const loadTxns = useCallback(async () => {
    setLoading(true);
    try {
      const params = {};
      // A failed payment moved no money, so it is not in the review queue
      // (§0.19); it has a tab of its own where it can be skipped.
      if (filter === 'unposted')  { params.is_posted_to_gl = false; params.is_skipped = false; params.failed = false; }
      else if (filter === 'posted')  { params.is_posted_to_gl = true; }
      else if (filter === 'skipped') { params.is_skipped = true; }
      else if (filter === 'partial') { params.is_posted_to_gl = false; params.is_skipped = false; params.failed = false; params.has_partial = true; }
      else if (filter === 'failed') { params.is_posted_to_gl = false; params.is_skipped = false; params.failed = true; }

      params.page_size = 5000;
      params.ordering = '-posted_date';
      // Each open row comes back with the suggester's best answer on it —
      // confidence, the account that is not cash, and why — computed once
      // for the whole page on the server.
      params.with_suggestions = 1;
      const res = await api.get('/reconciliation/bank-feed/', { params });
      let items = res.data.results || res.data || [];

      // Sort newest-first
      items.sort((a, b) => (b.posted_date || '').localeCompare(a.posted_date || ''));

      setTxns(items);
    } catch { setTxns([]); }
    finally { setLoading(false); }
  }, [filter]);

  const loadSummary = useCallback(async () => {
    try {
      const [allRes, unpostedRes, postedRes, skippedRes, failedRes] = await Promise.all([
        api.get('/reconciliation/bank-feed/', { params: { page_size: 1 } }),
        api.get('/reconciliation/bank-feed/', { params: { page_size: 1, is_posted_to_gl: false, is_skipped: false, failed: false } }),
        api.get('/reconciliation/bank-feed/', { params: { page_size: 1, is_posted_to_gl: true } }),
        api.get('/reconciliation/bank-feed/', { params: { page_size: 1, is_skipped: true } }),
        api.get('/reconciliation/bank-feed/', { params: { page_size: 1, is_posted_to_gl: false, is_skipped: false, failed: true } }),
      ]);
      const total = allRes.data.count || 0;
      const unposted = unpostedRes.data.count || 0;
      const posted = postedRes.data.count || 0;
      const skipped = skippedRes.data.count || 0;
      const failed = failedRes.data.count || 0;
      // Count partially allocated (have posted_description starting with 'Partial' but not posted)
      let partial = 0;
      try {
        const partialRes = await api.get('/reconciliation/bank-feed/', { params: { page_size: 1, is_posted_to_gl: false, is_skipped: false, failed: false, has_partial: true } });
        partial = partialRes.data.count || 0;
      } catch { /* endpoint may not support has_partial yet */ }
      setSummary({ total, unposted, posted, skipped, partial, failed });
    } catch { }
  }, []);

  const loadDeals = useCallback(async () => {
    try { const res = await api.get('/securities/deals/'); setDeals(res.data.results || res.data || []); } catch {}
  }, []);

  const loadEntities = useCallback(async () => {
    try { const res = await api.get('/core/entities/'); setEntities(res.data.results || res.data || []); } catch {}
  }, []);

  useEffect(() => { loadTxns(); }, [loadTxns]);

  // Auto-sync Mercury if last transaction is more than 6 hours old
  useEffect(() => {
    const autoSync = async () => {
      try {
        const res = await api.get('/reconciliation/bank-feed/', { params: { page_size: 1, ordering: '-posted_date' } });
        const items = res.data.results || res.data || [];
        if (items.length === 0) return;
        const lastDate = items[0].posted_date || items[0].created_date;
        if (!lastDate) return;
        const lastTime = new Date(lastDate + 'T23:59:59').getTime();
        const sixHoursAgo = Date.now() - (6 * 60 * 60 * 1000);
        if (lastTime < sixHoursAgo) {
          console.log('[AutoSync] Bank feed stale, syncing Mercury...');
          await api.post('/integrations/mercury/sync-bank-feed/');
          console.log('[AutoSync] Mercury sync complete');
          loadSummary();
        }
      } catch (err) {
        console.warn('[AutoSync] Mercury auto-sync failed:', err.message);
      }
    };
    autoSync();
  }, []);  // eslint-disable-line react-hooks/exhaustive-deps
  useEffect(() => {
    loadSummary(); loadDeals(); loadEntities(); loadAccountMap(); loadTransferSummary();
  }, [loadSummary, loadDeals, loadEntities, loadAccountMap, loadTransferSummary]);

  /* ── run server-side transfer detection ──────────────── */
  const runDetectTransfers = async () => {
    if (!window.confirm('Run transfer detection? This will classify all transactions as internal transfers or external.')) return;
    setDetecting(true);
    try {
      const res = await api.post('/reconciliation/bank-feed/detect-transfers/');
      alert(`Server-side detection complete: ${res.data.pairs_found} transfer pair(s) found, ${res.data.external_count} external transactions.`);
      loadTxns(); loadSummary(); loadTransferSummary();
    } catch (e) {
      alert('Transfer detection failed: ' + (e.response?.data?.error || e.message));
    } finally { setDetecting(false); }
  };


  const handleMercurySync = async () => {
    setSyncing(true);
    try {
      const res = await api.post('/integrations/mercury/sync-bank-feed/');
      const d = res.data;
      alert('Mercury Sync Complete\n\nFetched: ' + d.fetched_from_api + ' transactions\nNew: ' + d.new_inserted + '\nAlready existed: ' + d.already_existed + '\nTotal records: ' + d.total_records + '\nDate range: ' + (d.date_range?.earliest || '') + ' to ' + (d.date_range?.latest || ''));
      loadTxns(); loadSummary(); loadTransferSummary();
    } catch (err) {
      alert('Mercury sync failed: ' + (err.response?.data?.error || err.message));
    } finally { setSyncing(false); }
  };
  /* ── filtered transactions ───────────────────────────── */
  const filteredTxns = useMemo(() => {
    let items = txns;

    // Filter by account
    if (accountFilter !== 'all') {
      items = items.filter(t => t.mercury_account_id === accountFilter);
    }

    // Filter by transfer type
    if (transferFilter === 'external') {
      items = items.filter(t => (t._transferType || t.transfer_type) === 'external' || !(t._transferType || t.transfer_type));
    } else if (transferFilter === 'transfers') {
      items = items.filter(t => {
        const tt = t._transferType || t.transfer_type;
        return tt === 'internal_out' || tt === 'internal_in';
      });
    }

    // Filter by the suggester's confidence. Posted and skipped rows carry no
    // suggestion, so on those tabs anything but "all" empties the list —
    // which is the honest answer: nothing there is waiting to be posted.
    if (suggestionFilter !== 'all') {
      items = items.filter(t => (t.suggestion?.confidence || null) === suggestionFilter);
    }

    // Free-text search (client-side, multi-field)
    const q = searchQuery.trim().toLowerCase();
    if (q) {
      const qNum = q.replace(/[$,\s]/g, '');
      items = items.filter(t => {
        const acctName = (t.mercury_account_name || accountMap[t.mercury_account_id] || '').toLowerCase();
        const tt = (t._transferType || t.transfer_type || '').toLowerCase();
        const amtStr = String(t.amount || '').toLowerCase();
        return (
          (t.counterparty_name || '').toLowerCase().includes(q) ||
          (t.bank_description || '').toLowerCase().includes(q) ||
          (t.mercury_category || '').toLowerCase().includes(q) ||
          (t.status || '').toLowerCase().includes(q) ||
          (t.kind || '').toLowerCase().includes(q) ||
          acctName.includes(q) ||
          tt.includes(q) ||
          (qNum && amtStr.includes(qNum))
        );
      });
    }

    return items;
  }, [txns, accountFilter, transferFilter, suggestionFilter, searchQuery, accountMap]);

  /* ── how many rows sit at each confidence, for the filter's labels ── */
  const suggestionCounts = useMemo(() => {
    const counts = {};
    txns.forEach(t => {
      const c = t.suggestion?.confidence;
      if (c) counts[c] = (counts[c] || 0) + 1;
    });
    return counts;
  }, [txns]);

  /* ── keep currentPage within range when filters shrink results ── */
  useEffect(() => {
    const maxPage = Math.max(1, Math.ceil(filteredTxns.length / PAGE_SIZE));
    if (currentPage > maxPage) setCurrentPage(maxPage);
  }, [filteredTxns.length, currentPage]);

  /* ── client-side money in/out summary ────────────────── */
  const moneyStats = useMemo(() => {
    let extIn = 0, extOut = 0, xferTotal = 0, extInCount = 0, extOutCount = 0, xferCount = 0;

    txns.forEach(t => {
      const tt = t._transferType || t.transfer_type || 'external';
      const amt = parseFloat(t.amount);
      if (tt === 'internal_out' || tt === 'internal_in') {
        xferCount++;
        xferTotal += Math.abs(amt);
      } else {
        if (amt > 0) { extIn += amt; extInCount++; }
        else { extOut += amt; extOutCount++; }
      }
    });

    return {
      extIn, extOut, extNet: extIn + extOut,
      extInCount, extOutCount,
      xferTotal, xferCount,
    };
  }, [txns]);

  /* ── unique accounts in current data ─────────────────── */
  const accountOptions = useMemo(() => {
    const ids = [...new Set(txns.map(t => t.mercury_account_id).filter(Boolean))];
    return ids.map(id => ({
      id,
      name: accountMap[id] || t_mercury_account_name(txns, id) || id.substring(0, 8),
    }));
  }, [txns, accountMap]);

  // The accounts the book actually posts to, so the picker can offer the
  // per-owner ones the chart cannot know about.
  useEffect(() => {
    api.get('/ledger/entries/accounts-in-use/')
      .then(r => setAccountsInUse(r.data.accounts || []))
      .catch(() => {});
  }, []);

  // The chart of accounts itself, from the server. A failure here is not fatal
  // — GLSelect falls back to a short built-in list — but it does mean the
  // picker is missing most of its accounts, so it is worth not swallowing
  // silently in the console.
  useEffect(() => {
    api.get('/ledger/entries/chart-of-accounts/')
      .then(r => setChart(r.data.sections || []))
      .catch(e => { console.warn('Chart of accounts unavailable; using fallback list.', e); });
  }, []);

  // Flattened once per chart change rather than on every render of every
  // select — an allocation modal renders two of these per line.
  const chartByValue = useMemo(
    () => flattenChart(chart.length ? chart : FALLBACK_CHART),
    [chart],
  );

  /* ── actions ─────────────────────────────────────────── */
  const bulkSkip = async () => {
    const ids = Array.from(selected);
    if (!ids.length || !window.confirm(`Skip ${ids.length} transaction(s)?`)) return;
    try {
      await api.post('/reconciliation/bank-feed/bulk-skip/', { txn_ids: ids });
      setSelected(new Set()); loadTxns(); loadSummary();
    } catch (e) { alert('Failed: ' + e.message); }
  };

  const bulkPost = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const unallocated = txns.filter(t => ids.includes(t.mercury_txn_id) && !t.is_fully_allocated);
    if (unallocated.length > 0) {
      alert(`${unallocated.length} transaction(s) are not fully allocated. Review them first.`);
      return;
    }
    if (!window.confirm(`Post ${ids.length} transaction(s) to the General Ledger?`)) return;
    setPosting(true);
    try {
      const res = await api.post('/reconciliation/bank-feed/bulk-post/', { txn_ids: ids });
      const errors = res.data.errors || [];
      let msg = `Posted ${res.data.posted} of ${ids.length}.`;
      if (errors.length) {
        // Each refusal names the counterparty and the date, not a row id.
        msg += `\n\nNot posted:\n` + errors.slice(0, 12).map(e =>
          `• ${e.counterparty || e.txn_id}${e.posted_date ? ` (${fmtDate(e.posted_date)})` : ''} — ${e.error}`
        ).join('\n') + (errors.length > 12 ? `\n…and ${errors.length - 12} more` : '');
      }
      alert(msg);
      setSelected(new Set()); loadTxns(); loadSummary();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
    finally { setPosting(false); }
  };

  /* Write the suggester's answer as the allocation line on every selected
     row, so the rows can then be posted with Post to GL. The server
     recomputes each suggestion and applies only those at or above the bar —
     "Confident", or "Confident or Likely" when the filter is set to Likely —
     and leaves alone anything already allocated by hand. Nothing is posted
     here; the lines are drafts, editable in Review. */
  const applySuggestions = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const minConf = suggestionFilter === 'medium' ? 'medium' : 'high';
    const bar = minConf === 'medium' ? 'Confident or Likely' : 'Confident';
    if (!window.confirm(
      `Fill in the suggested accounts on ${ids.length} transaction(s)?\n\n` +
      `Only rows whose best suggestion is ${bar} are filled in, and rows that already ` +
      `have allocation lines are left alone. Nothing is posted until you press Post to GL.`
    )) return;
    setApplying(true);
    try {
      const res = await api.post('/reconciliation/bank-feed/apply-suggestions/',
        { txn_ids: ids, min_confidence: minConf });
      const skipped = res.data.skipped || [];
      let msg = `Filled in ${res.data.applied} of ${ids.length}. Check them, then Post to GL.`;
      if (skipped.length) {
        msg += `\n\nLeft alone:\n` + skipped.slice(0, 12).map(sk =>
          `• ${sk.counterparty || sk.txn_id}${sk.posted_date ? ` (${fmtDate(sk.posted_date)})` : ''} — ${sk.reason}`
        ).join('\n') + (skipped.length > 12 ? `\n…and ${skipped.length - 12} more` : '');
      }
      alert(msg);
      // Keep the selection: the next click is Post to GL on the same rows.
      loadTxns(); loadSummary();
    } catch (e) { alert('Failed: ' + (e.response?.data?.error || e.message)); }
    finally { setApplying(false); }
  };

  const toggleSelect = (id) => {
    setSelected(prev => { const next = new Set(prev); next.has(id) ? next.delete(id) : next.add(id); return next; });
  };

  const toggleSelectAll = () => {
    setSelected(prev => prev.size === filteredTxns.length ? new Set() : new Set(filteredTxns.map(t => t.mercury_txn_id)));
  };

  /* ── RENDER ──────────────────────────────────────────── */
  const totalPages = Math.max(1, Math.ceil(filteredTxns.length / PAGE_SIZE));
  const pageRows = filteredTxns.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);
  const pageStart = filteredTxns.length === 0 ? 0 : (currentPage - 1) * PAGE_SIZE + 1;
  const pageEnd = Math.min(currentPage * PAGE_SIZE, filteredTxns.length);
  const showCheckbox = filter === 'unposted' || filter === 'failed';
  const colCount = 9 + (showCheckbox ? 1 : 0);

  return (
    <div style={{ fontFamily: theme.fontBody, color: theme.text }}>
      <PageHeader
        title="Bank Feed"
        subtitle="Review, allocate and post bank transactions to the General Ledger"
        action={
          <>
            <Button variant="ghost" onClick={() => { loadTxns(); loadSummary(); loadTransferSummary(); }}>
              Refresh
            </Button>
            <Button variant="secondary" onClick={runDetectTransfers} disabled={detecting}>
              {detecting ? 'Detecting…' : 'Detect Transfers'}
            </Button>
            <Button variant="secondary" onClick={() => setShowCitiImport(true)}>
              Import Citi CSV
            </Button>
            <Button onClick={handleMercurySync} disabled={syncing}>
              {syncing ? 'Syncing…' : 'Sync Mercury'}
            </Button>
          </>
        }
      />

      {/* Bank balances and treasury, moved here from the Dashboard on the
          usability review of 23 October 2026 (item 14). Folds away, and stays
          folded if the reader leaves it that way. */}
      <BankBalancesPanel />

      {/* KPIs — the same StatCard row Ledger and the Dashboard use. Posting
          status lives on the tabs below as counts, not as five more cards. */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap' }}>
        <StatCard label="Awaiting review" value={(summary.unposted || 0).toLocaleString()}
          accent={summary.unposted ? theme.accent : theme.success} />
        <StatCard label={`Money in (${moneyStats.extInCount})`} value={fmt(moneyStats.extIn)}
          color={theme.success} accent={theme.success} />
        <StatCard label={`Money out (${moneyStats.extOutCount})`} value={fmt(moneyStats.extOut)}
          color={theme.danger} accent={theme.danger} />
        <StatCard label="Net cash flow" value={fmt(moneyStats.extNet)}
          color={moneyStats.extNet >= 0 ? theme.success : theme.danger}
          accent={moneyStats.extNet >= 0 ? theme.success : theme.danger} />
        <StatCard label={`Internal transfers (${moneyStats.xferCount})`} value={fmt(moneyStats.xferTotal)}
          color={theme.info} accent={theme.info}
          change="between our own accounts — excluded from in and out" />
      </div>

      <Tabs
        tabs={[
          { key: 'unposted', label: 'Unposted', count: summary.unposted },
          { key: 'partial', label: 'Partially allocated', count: summary.partial },
          { key: 'posted', label: 'Posted', count: summary.posted },
          { key: 'skipped', label: 'Skipped', count: summary.skipped },
          { key: 'failed', label: 'Failed or dropped (not posted)', count: summary.failed },
          { key: 'all', label: 'All', count: summary.total },
        ]}
        active={filter}
        onChange={f => { setFilter(f); setSelected(new Set()); setCurrentPage(1); }}
      />

      {filter === 'failed' && (
        <Card style={{ marginBottom: 16 }}>
          <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 6 }}>
            Anything booked off a payment that never moved?
          </div>
          {!unsettledAudit && <div style={{ fontSize: 12, color: theme.textMuted }}>Checking the ledger and the capital-source register…</div>}
          {unsettledAudit?.error && <div style={{ fontSize: 12, color: C.red }}>The check could not run. Reload the page to try again.</div>}
          {unsettledAudit && !unsettledAudit.error && (
            unsettledAudit.findings.length === 0 ? (
              <div style={{ fontSize: 12, color: C.green, fontWeight: 600 }}>
                Nothing. All {unsettledAudit.rows_checked} failed, pending, dropped, cancelled, reversed or blocked rows
                were checked against the ledger and the capital-source register, and none has anything booked off it.
              </div>
            ) : (
              <>
                <div style={{ fontSize: 12, color: C.red, fontWeight: 600, marginBottom: 8 }}>
                  {unsettledAudit.findings.length} of {unsettledAudit.rows_checked} rows have something booked off them.
                </div>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                  <thead><tr>
                    {['Date', 'Counterparty', 'Account', 'Status', 'Amount', 'What was booked'].map(h =>
                      <th key={h} style={{ textAlign: h === 'Amount' ? 'right' : 'left', padding: '4px 8px', color: theme.textMuted }}>{h}</th>)}
                  </tr></thead>
                  <tbody>
                    {unsettledAudit.findings.map(f => (
                      <tr key={f.mercury_txn_id} style={{ borderTop: `1px solid ${theme.border}` }}>
                        <td style={{ padding: '4px 8px' }}>{fmtDate(f.posted_date)}</td>
                        <td style={{ padding: '4px 8px' }}>{f.counterparty}</td>
                        <td style={{ padding: '4px 8px' }}>{f.entity} {f.account}</td>
                        <td style={{ padding: '4px 8px', textTransform: 'capitalize' }}>{f.status}</td>
                        <td style={{ padding: '4px 8px', textAlign: 'right', fontFamily: theme.fontMono, whiteSpace: 'nowrap' }}>{fmtCcy(f.amount, f.currency)}</td>
                        <td style={{ padding: '4px 8px' }}>{({
    on_ledger: 'Posted to the GL, and the posting still stands',
    capital_source_journal: 'A capital source was raised off it, with a live journal',
    capital_source_no_journal: 'A capital source points at it (no journal)',
    flag_only: 'Marked posted with nothing on the ledger behind it',
  })[f.finding] || f.finding}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )
          )}
        </Card>
      )}

      {/* Filters */}
      <Card noPad style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', gap: 10, padding: '12px 16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <select value={transferFilter} onChange={e => setTransferFilter(e.target.value)} style={filterControlStyle}>
            <option value="all">All types</option>
            <option value="external">External only</option>
            <option value="transfers">Internal transfers only</option>
          </select>
          <select value={suggestionFilter}
            onChange={e => { setSuggestionFilter(e.target.value); setSelected(new Set()); setCurrentPage(1); }}
            style={filterControlStyle}
            title={'How sure the suggester is about each row. Confident: booked this counterparty the same way ' +
                   'before, or the feed itself says what it is. Likely: a single earlier posting, or the wording ' +
                   'names an account. Needs splitting: more than one account fits. Cannot tell: nothing on the row is recognised.'}>
            <option value="all">Any suggestion</option>
            {CERTAINTY_ORDER.map(c => (
              <option key={c} value={c}>{CERTAINTY[c]} ({(suggestionCounts[c] || 0).toLocaleString()})</option>
            ))}
          </select>
          <select value={accountFilter} onChange={e => setAccountFilter(e.target.value)}
            style={{ ...filterControlStyle, maxWidth: 260 }}>
            <option value="all">All accounts ({accountOptions.length})</option>
            {accountOptions.map(a => (
              <option key={a.id} value={a.id}>{a.name}</option>
            ))}
          </select>
          <div style={{ position: 'relative', display: 'inline-block' }}>
            <input type="text" placeholder="Search counterparty, description, account, amount…"
              value={searchQuery} onChange={e => setSearchQuery(e.target.value)}
              style={{ ...filterControlStyle, cursor: 'text', width: 320, paddingRight: 28, outline: 'none' }} />
            {searchQuery && (
              <button onClick={() => setSearchQuery('')} title="Clear search"
                style={{ position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)',
                         background: 'none', border: 'none', color: theme.textMuted, cursor: 'pointer',
                         fontSize: 14, lineHeight: 1, padding: '2px 6px' }}>×</button>
            )}
          </div>
          <span style={{ color: theme.textMuted, fontSize: 12 }}>
            {filteredTxns.length.toLocaleString()} of {txns.length.toLocaleString()} transactions
          </span>
          <ExportCsvButton
            filename="bank-feed"
            rows={filteredTxns}
            columns={[
              { label: 'Date', value: (r) => r.posted_date || r.created_date },
              { label: 'Account', value: (r) => r.bank_account_label },
              { label: 'Entity', value: (r) => r.entity_short_name },
              { label: 'Counterparty', value: (r) => r.counterparty_name },
              { label: 'Description', value: (r) => r.bank_description },
              { label: 'Amount', value: (r) => r.amount },
              { label: 'Currency', value: (r) => r.currency },
              { label: 'Status', value: (r) => r.status },
              { label: 'Posted to ledger', value: (r) => (r.is_posted_to_gl ? 'yes' : 'no') },
              { label: 'Skipped', value: (r) => (r.is_skipped ? 'yes' : 'no') },
            ]}
          />

          {selected.size > 0 && showCheckbox && (
            <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, alignItems: 'center' }}>
              <span style={{ fontSize: 12, color: theme.textSecondary }}>{selected.size} selected</span>
              <Button variant="secondary" size="sm" onClick={bulkSkip}>Skip</Button>
              {filter !== 'failed' && (<>
              <Button variant="secondary" size="sm" onClick={applySuggestions} disabled={applying}
                title={'Write the suggested accounts as the allocation line on the selected rows ' +
                       `(${suggestionFilter === 'medium' ? 'Confident or Likely' : 'Confident'} only). ` +
                       'Nothing is posted until you press Post to GL.'}>
                {applying ? 'Filling in…' : 'Apply suggestions'}
              </Button>
              <Button variant="secondary" size="sm" onClick={() => setShowBookAll(true)}
                title="Write one booking — the same account, deal and note — on every selected row, and post them in the same go if you want.">
                Book all as…
              </Button>
              <Button variant="success" size="sm" onClick={bulkPost} disabled={posting}>
                {posting ? 'Posting…' : 'Post to GL'}
              </Button>
              </>)}
            </div>
          )}
        </div>
      </Card>

      {/* Table */}
      <Card noPad>
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13, fontFamily: theme.fontBody }}>
            <thead>
              <tr>
                {showCheckbox && (
                  <th style={{ ...feedThStyle, width: 32 }}>
                    <input type="checkbox" checked={selected.size === filteredTxns.length && filteredTxns.length > 0}
                      onChange={toggleSelectAll} />
                  </th>
                )}
                <th style={feedThStyle}>Date</th>
                <th style={feedThStyle}>Account</th>
                <th style={feedThStyle}>Counterparty</th>
                <th style={feedThStyle}>Description</th>
                <th style={{ ...feedThStyle, textAlign: 'right' }}>Amount</th>
                <th style={feedThStyle}>Status</th>
                <th style={feedThStyle}>Allocations</th>
                <th style={feedThStyle}>Suggested</th>
                <th style={feedStickyThStyle}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={colCount} style={emptyStyle}>Loading…</td></tr>
              ) : filteredTxns.length === 0 ? (
                <tr><td colSpan={colCount} style={emptyStyle}>
                  {txns.length === 0
                    ? `No ${filter === 'all' ? '' : filter + ' '}transactions.`
                    : 'No transactions match these filters.'}
                </td></tr>
              ) : pageRows.map(txn => {
                const isIn = parseFloat(txn.amount) > 0;
                const isSel = selected.has(txn.mercury_txn_id);
                const allocCount = txn.allocation_count != null
                  ? txn.allocation_count
                  : (txn.allocations || []).length;
                const isAllocated = txn.is_fully_allocated || txn.is_posted_to_gl;
                const tt = txn._transferType || txn.transfer_type;
                const isTransfer = tt === 'internal_out' || tt === 'internal_in';
                const acct = accountLabel(txn, accountMap);
                const acctName = acct.raw;
                // Solid, never transparent: the pinned Actions cell inherits
                // this and the scrolling columns would show through anything
                // translucent.
                const rowBg = isSel ? SELECTED_ROW_BG : isTransfer ? TRANSFER_ROW_BG : theme.bgCard;

                return (
                  <tr key={txn.mercury_txn_id}
                    style={{ background: rowBg, transition: theme.transitionFast }}
                    onMouseEnter={e => { if (!isSel) e.currentTarget.style.background = theme.tableRowHover; }}
                    onMouseLeave={e => { if (!isSel) e.currentTarget.style.background = rowBg; }}
                  >
                    {showCheckbox && (
                      <td style={feedTdStyle}>
                        <input type="checkbox" checked={isSel} onChange={() => toggleSelect(txn.mercury_txn_id)} />
                      </td>
                    )}
                    <td style={feedTdStyle}>{fmtDate(txn.posted_date || txn.created_date)}</td>
                    {/* Whose money over which account, so the cell is two
                        short lines rather than one long one cut off with an
                        ellipsis. The account line never breaks: "Checking"
                        over "••6816" reads as two accounts. */}
                    <td style={{ ...feedWrapTdStyle, fontSize: 12, lineHeight: 1.35, maxWidth: 170 }}
                      title={`${sourceName(txn.source)} — ${acct.entity ? acct.entity + ' — ' : ''}${acctName}`}>
                      {acct.entity && <div style={{ fontWeight: 600, color: theme.text }}>{acct.entity}</div>}
                      <div style={{ color: theme.textSecondary, whiteSpace: 'nowrap' }}>
                        <BankDot source={txn.source} />{acct.short}
                      </div>
                    </td>
                    <td style={{ ...feedWrapTdStyle, fontWeight: 600, lineHeight: 1.35, maxWidth: 220 }}>
                      {txn.counterparty_name || '—'}
                    </td>
                    {/* A wire memo can carry a 32-character reference with no
                        space in it ("/PURP/CSDB///ROC/IBCD…"), which would hold
                        the column open on its own, so this one may break
                        anywhere. The category, when there is one (Citi's
                        Domestic / Foreign; blank on every Mercury row), rides
                        underneath rather than holding a column of dashes. */}
                    <td style={{ ...feedWrapTdStyle, overflowWrap: 'anywhere', fontSize: 12, color: theme.textSecondary,
                                 lineHeight: 1.35, minWidth: 130, maxWidth: 300 }}>
                      {txn.bank_description || '—'}
                      {txn.mercury_category && (
                        <div style={{ fontSize: 11, color: theme.textMuted }}>{txn.mercury_category}</div>
                      )}
                    </td>
                    {/* The direction rides under the figure it describes. It
                        had a column of its own, and that column was what the
                        table could least afford at a laptop's width. */}
                    <td style={{ ...feedTdStyle, textAlign: 'right', fontFamily: theme.fontMono, fontSize: 12,
                                 fontWeight: 500, fontVariantNumeric: 'tabular-nums',
                                 color: isTransfer ? theme.textSecondary : isIn ? theme.success : theme.danger }}>
                      {fmtCcy(txn.amount, txn.currency)}
                      <span style={{ display: 'block', marginTop: 3 }}>
                        {isTransfer ? (
                          <span title={tt === 'internal_out'
                            ? 'Money sent to another of our own accounts'
                            : 'Money received from another of our own accounts'}>
                            <Badge text={tt === 'internal_out' ? 'Transfer out' : 'Transfer in'} variant="blue" dot />
                          </span>
                        ) : (
                          <Badge text={isIn ? 'In' : 'Out'} variant={isIn ? 'green' : 'red'} />
                        )}
                      </span>
                      {txn.currency && txn.currency !== 'USD' && (() => {
                        // Which of the three figures this row will book at, said
                        // on the row rather than left to be found in the modal.
                        const b = postingBasis(txn);
                        if (b.source === 'reviewer') return (
                          <span style={{ display: 'block', marginTop: 3 }} title={
                            `Books at ${fmt(txn.usd_amount_override)}, a rate of ` +
                            `${impliedRate(txn.usd_amount_override, txn.amount)} confirmed by hand.` +
                            (txn.usd_override_source ? ` Source: ${txn.usd_override_source}.` : '')
                          }>
                            <Badge variant="green" text={`= ${fmt(txn.usd_amount_override)}`} />
                          </span>
                        );
                        return (
                          <span style={{ display: 'block', marginTop: 3 }} title={
                            `This amount is in ${txn.currency} and cannot be posted until ` +
                            `somebody confirms what it cost in dollars.` +
                            (txn.usd_amount
                              ? ` Citi's export suggests ${fmt(txn.usd_amount)} at ${txn.fx_rate} —` +
                                ` indicative, and accepting it is a decision the book will record.`
                              : ' The export carries no rate.') +
                            ' Open Review to set it.'
                          }>
                            <Badge variant="red" text={`${txn.currency} — needs $`} />
                          </span>
                        );
                      })()}
                    </td>
                    <td style={{ ...feedTdStyle, fontSize: 12 }}>
                      {(txn.status || '').toLowerCase() === 'pending'
                        ? <Badge text="Pending" variant="yellow" />
                        : <span style={{ color: theme.textMuted, textTransform: 'capitalize' }}>{txn.status}</span>}
                    </td>
                    <td style={feedTdStyle}>
                      {allocCount > 0 ? (
                        <Badge variant={isAllocated ? 'green' : 'yellow'}
                          text={`${allocCount} line${allocCount > 1 ? 's' : ''}`} />
                      ) : (
                        <span style={{ color: theme.textMuted, fontSize: 12 }}>None</span>
                      )}
                    </td>
                    <td style={{ ...feedWrapTdStyle, maxWidth: 230 }}>
                      {txn.suggestion ? (() => {
                        // The same verdict the Review card opens onto, cut to
                        // a confidence and the account that is not cash.
                        const sg = txn.suggestion;
                        const tip = `${sg.label}\nDR ${sg.debit_account || '— you choose —'}` +
                          `\nCR ${sg.credit_account || '— you choose —'}\n${sg.why}` +
                          (sg.needs_input ? `\n⚠ ${sg.needs_input}` : '');
                        return (
                          <div title={tip} style={{ display: 'flex', flexDirection: 'column', gap: 3, alignItems: 'flex-start' }}>
                            <Badge text={CERTAINTY[sg.confidence] || sg.confidence}
                              variant={CERTAINTY_VARIANT[sg.confidence] || 'gray'} />
                            {sg.other_account && (
                              <span style={{ fontSize: 11, lineHeight: 1.35, color: theme.textSecondary, fontFamily: theme.fontMono }}>
                                {breakableAccount(sg.other_account)}
                              </span>
                            )}
                          </div>
                        );
                      })() : (
                        <span style={{ color: theme.textMuted, fontSize: 12 }}>—</span>
                      )}
                    </td>
                    <td style={{ ...feedStickyTdStyle, background: 'inherit' }}>
                      <Button variant="secondary" size="sm" onClick={() => setEditTxn(txn)}>Review</Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Pagination — the same footer FinTable draws. */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between',
                      padding: '10px 16px', borderTop: `1px solid ${theme.borderLight}`,
                      fontSize: 12, color: theme.textMuted }}>
          <span>{pageStart}–{pageEnd} of {filteredTxns.length.toLocaleString()}</span>
          <div style={{ display: 'flex', gap: 4, alignItems: 'center' }}>
            {[
              { label: '«', onClick: () => setCurrentPage(1), disabled: currentPage <= 1 },
              { label: '‹', onClick: () => setCurrentPage(p => Math.max(1, p - 1)), disabled: currentPage <= 1 },
            ].map((b, i) => (
              <button key={i} onClick={b.onClick} disabled={b.disabled} style={pagerBtnStyle(b.disabled)}>{b.label}</button>
            ))}
            <span style={{ padding: '0 10px', color: theme.text, fontWeight: 600 }}>
              Page {currentPage} of {totalPages}
            </span>
            {[
              { label: '›', onClick: () => setCurrentPage(p => Math.min(totalPages, p + 1)), disabled: currentPage >= totalPages },
              { label: '»', onClick: () => setCurrentPage(totalPages), disabled: currentPage >= totalPages },
            ].map((b, i) => (
              <button key={i} onClick={b.onClick} disabled={b.disabled} style={pagerBtnStyle(b.disabled)}>{b.label}</button>
            ))}
          </div>
          <span>{PAGE_SIZE} per page</span>
        </div>
      </Card>

      {/* Citi CSV import */}
      {showCitiImport && (
        <CitiImportModal
          entities={entities}
          onClose={() => setShowCitiImport(false)}
          onImported={() => { setShowCitiImport(false); loadTxns(); loadSummary(); }}
        />
      )}

      {/* Book all as… — one booking on every selected row */}
      {showBookAll && (
        <BookAllModal
          rows={txns.filter(t => selected.has(t.mercury_txn_id))}
          deals={deals} entities={entities} accountsInUse={accountsInUse}
          chart={chart} chartByValue={chartByValue} cashInfo={cashInfo}
          onClose={() => setShowBookAll(false)}
          onDone={() => { setShowBookAll(false); setSelected(new Set()); loadTxns(); loadSummary(); }}
        />
      )}

      {/* Review Modal */}
      {editTxn && (
        <AllocationModal txn={editTxn} deals={deals} entities={entities} accountsInUse={accountsInUse}
          chart={chart} chartByValue={chartByValue} cashInfo={cashInfo}
          onClose={() => setEditTxn(null)}
          onSaved={() => { setEditTxn(null); loadTxns(); loadSummary(); }}
          onPosted={() => { setEditTxn(null); loadTxns(); loadSummary(); }}
        />
      )}
    </div>
  );
}

/* ── Helper: get account name from txns array ── */
function t_mercury_account_name(txns, id) {
  const t = txns.find(t => t.mercury_account_id === id && t.mercury_account_name);
  return t ? t.mercury_account_name : null;
}


/* ═══════════════════ CITI CSV IMPORT MODAL ══════════════════════
   Citi offers Helena neither an API nor an SFTP drop, so the feed is a person
   exporting the Transaction Dashboard from CitiDirect and uploading it here.

   Two steps, deliberately. The first is a dry run that writes nothing and
   reports what the file contains, which accounts it found and which rows it
   cannot state in dollars. The mapping of a Citi account to an entity is
   confirmed by a person on that screen, because getting it wrong puts every
   transaction in the account onto the wrong set of books — and once a row is
   posted, the ledger cannot delete it.
   ═══════════════════════════════════════════════════════════════ */
function CitiImportModal({ entities, onClose, onImported }) {
  const [tab, setTab] = useState('transactions');
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState(null);
  const [accountMap, setAccountMap] = useState({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  const runDryRun = async (f) => {
    setBusy(true); setError(''); setPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('dry_run', 'true');
      const res = await api.post('/integrations/citi/import-transactions/', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setPreview(res.data);
      // Seed the mapping with whatever the server could work out itself.
      const seed = {};
      (res.data.accounts || []).forEach(a => {
        if (a.entity_id) seed[a.account_number] = String(a.entity_id);
      });
      setAccountMap(seed);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  const onPick = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFile(f);
    runDryRun(f);
  };

  const unmapped = (preview?.accounts || []).filter(a => !accountMap[a.account_number]);
  const canImport = preview && preview.new > 0 && unmapped.length === 0 && !busy;

  const runImport = async () => {
    if (!canImport) return;
    setBusy(true); setError('');
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('dry_run', 'false');
      fd.append('register_accounts', 'true');
      fd.append('account_map', JSON.stringify(accountMap));
      const res = await api.post('/integrations/citi/import-transactions/', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      alert(
        `Imported ${res.data.created} new Citi transaction(s).\n` +
        `${res.data.already_present} were already in the feed and were left alone.\n` +
        (res.data.needs_fx_review
          ? `\n${res.data.needs_fx_review} row(s) are not in dollars — open each one before posting.`
          : '')
      );
      onImported();
    } catch (e) {
      setError(e.response?.data?.error || e.message);
      setBusy(false);
    }
  };

  /* ── Balances ── */
  const [balFile, setBalFile] = useState(null);
  const [balPreview, setBalPreview] = useState(null);
  const [balBusy, setBalBusy] = useState(false);
  const [balError, setBalError] = useState('');

  const runBalanceDryRun = async (f) => {
    setBalBusy(true); setBalError(''); setBalPreview(null);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('dry_run', 'true');
      const res = await api.post('/integrations/citi/import-balances/', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      setBalPreview(res.data);
    } catch (e) {
      setBalError(e.response?.data?.error || e.message);
    } finally { setBalBusy(false); }
  };

  const onPickBalance = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBalFile(f);
    runBalanceDryRun(f);
  };

  const runBalanceImport = async () => {
    if (!balPreview || balBusy) return;
    setBalBusy(true); setBalError('');
    try {
      const fd = new FormData();
      fd.append('file', balFile);
      fd.append('dry_run', 'false');
      const res = await api.post('/integrations/citi/import-balances/', fd,
        { headers: { 'Content-Type': 'multipart/form-data' } });
      alert(
        `Recorded ${res.data.loaded} balance(s).\n` +
        `${res.data.reconciled} tie to the staged transactions exactly` +
        (res.data.reconciliation_breaks
          ? `, ${res.data.reconciliation_breaks} do not — see the detail.`
          : '.')
      );
      onImported();
    } catch (e) {
      setBalError(e.response?.data?.error || e.message);
      setBalBusy(false);
    }
  };

  // The same tab buttons Ledger puts in its header.
  const tabStyle = (name) => ({
    padding: '6px 14px', borderRadius: theme.radius, fontFamily: theme.fontBody, cursor: 'pointer',
    border: `1px solid ${tab === name ? theme.accent : theme.border}`,
    background: tab === name ? theme.accent : theme.bgCard,
    color: tab === name ? theme.textOnAccent : theme.text,
    fontSize: 12, fontWeight: 600,
  });

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 900 }} onClick={e => e.stopPropagation()}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Import from Citi</h2>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
              Citi offers no API and no SFTP, so both of these are CitiDirect exports.
              Nothing is written until you confirm.
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        <div style={{ display: 'flex', gap: 6, marginBottom: 16 }}>
          <button onClick={() => setTab('transactions')} style={tabStyle('transactions')}>Transactions</button>
          <button onClick={() => setTab('balances')} style={tabStyle('balances')}>Balances</button>
        </div>

        {tab === 'balances' ? (
          <CitiBalanceTab
            file={balFile} preview={balPreview} busy={balBusy} error={balError}
            onPick={onPickBalance} onImport={runBalanceImport} onClose={onClose}
          />
        ) : (
        <>

        <label style={{
          display: 'block', padding: 20, borderRadius: 8, textAlign: 'center',
          border: `1px dashed ${C.border}`, background: C.inputBg, cursor: 'pointer', marginBottom: 16,
        }}>
          <input type="file" accept=".csv,text/csv" onChange={onPick} style={{ display: 'none' }} />
          <div style={{ fontSize: 13, fontWeight: 600, color: C.accent }}>
            {file ? file.name : 'Choose the CitiDirect CSV export'}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
            {busy && !preview ? 'Reading…' : 'Re-uploading a file you have already imported is safe — rows already in the feed are left untouched.'}
          </div>
        </label>

        {error && (
          <div style={{
            padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 12,
            background: theme.dangerBg, border: `1px solid ${theme.dangerBorder}`, color: C.red,
          }}>{error}</div>
        )}

        {preview && (
          <>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
              {[
                ['Rows in file', preview.parsed, C.text],
                ['New to the feed', preview.new, C.green],
                ['Already imported', preview.already_present, C.muted],
                ['Not in dollars', preview.needs_fx_review, preview.needs_fx_review ? C.yellow : C.muted],
              ].map(([label, value, color]) => (
                <div key={label} style={{ padding: 12, background: C.inputBg, borderRadius: 8, border: `1px solid ${C.border}` }}>
                  <div style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase', fontWeight: 600 }}>{label}</div>
                  <div style={{ fontSize: 20, fontWeight: 700, color }}>{value}</div>
                </div>
              ))}
            </div>

            {preview.already_present > 0 && (
              <div style={{
                padding: '10px 14px', marginBottom: 14, borderRadius: 8, fontSize: 12,
                background: theme.successBg, border: `1px solid ${theme.successBorder}`,
              }}>
                <span style={{ fontWeight: 700, color: C.green }}>
                  {preview.already_present} row(s) are already in the feed and will be left exactly as they are
                </span>
                <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                  Anything you have reviewed, allocated or posted stays posted. Uploading the whole
                  history again is the intended way to use this.
                  {preview.matched_by_content > 0 && (
                    <> {preview.matched_by_content} of them were recognised by their date, account,
                    amount and counterparty rather than an exact match — this export formats some
                    reference numbers differently from the one they came in on, which changes
                    nothing about the payments.</>
                  )}
                </div>
              </div>
            )}

            {preview.date_range?.earliest && (
              <div style={{ fontSize: 12, color: C.muted, marginBottom: 16 }}>
                Covering {fmtDate(preview.date_range.earliest)} to {fmtDate(preview.date_range.latest)}
                {preview.internal_transfers > 0 && (
                  <> · {preview.internal_transfers} row(s) are transfers between your own Citi accounts and are flagged as such</>
                )}
              </div>
            )}

            {/* Account → entity mapping */}
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 4px' }}>
              Which entity owns each Citi account?
            </h3>
            <div style={{ fontSize: 11, color: C.muted, marginBottom: 8 }}>
              Every transaction in an account is booked to the entity you name here, so this
              has to be right before anything is imported.
            </div>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              {(preview.accounts || []).map(a => (
                <div key={a.account_number} style={{
                  display: 'grid', gridTemplateColumns: '1fr 1.2fr', gap: 10, alignItems: 'center',
                  padding: 10, background: C.inputBg, borderRadius: 8,
                  border: `1px solid ${accountMap[a.account_number] ? C.border : C.yellow}`,
                }}>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>
                      {a.short_code || '(no nickname)'}
                      <span style={{ color: C.muted, fontWeight: 500, marginLeft: 8, fontFamily: theme.fontMono, fontSize: 11 }}>
                        {a.account_number}
                      </span>
                    </div>
                    <div style={{ fontSize: 11, color: C.muted }}>
                      {a.row_count} row(s) · {a.match_reason}
                    </div>
                  </div>
                  <select
                    value={accountMap[a.account_number] || ''}
                    onChange={e => setAccountMap(m => ({ ...m, [a.account_number]: e.target.value }))}
                    style={inputStyle}
                    disabled={a.is_registered}
                    title={a.is_registered ? 'Already registered — change it in Settings' : ''}
                  >
                    <option value="">— choose the entity —</option>
                    {(entities || []).map(en => (
                      <option key={en.id} value={en.id}>{en.short_name || en.name}</option>
                    ))}
                  </select>
                </div>
              ))}
            </div>

            {preview.skipped?.length > 0 && (
              <div style={{
                padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 11,
                background: theme.warningBg, border: `1px solid ${theme.warningBorder}`,
              }}>
                <div style={{ fontWeight: 700, color: C.yellow, marginBottom: 4 }}>
                  {preview.skipped.length} row(s) could not be read and will not be imported
                </div>
                {preview.skipped.slice(0, 5).map((sk, i) => (
                  <div key={i} style={{ color: C.muted }}>Line {sk.line}: {sk.reason}</div>
                ))}
              </div>
            )}

            {/* What is about to be imported */}
            <h3 style={{ fontSize: 14, fontWeight: 600, margin: '0 0 8px' }}>
              Preview{preview.rows?.length > 12 ? ' (first 12)' : ''}
            </h3>
            <div style={{ maxHeight: 260, overflowY: 'auto', border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 18 }}>
              <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11 }}>
                <thead>
                  <tr>
                    {['Date', 'Account', 'Counterparty', 'Amount', ''].map(h => (
                      <th key={h} style={{ ...thStyle, padding: '6px 10px', fontSize: 9 }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {(preview.rows || []).slice(0, 12).map(r => (
                    <tr key={r.txn_id} style={{ opacity: r.is_new ? 1 : 0.45 }}>
                      <td style={{ ...tdStyle, padding: '4px 10px' }}>{fmtDate(r.date)}</td>
                      <td style={{ ...tdStyle, padding: '4px 10px' }}>{r.account}</td>
                      <td style={{ ...tdStyle, padding: '4px 10px', maxWidth: 200, overflow: 'hidden', textOverflow: 'ellipsis' }}>
                        {r.counterparty || '—'}
                      </td>
                      <td style={{
                        ...tdStyle, padding: '4px 10px', textAlign: 'right',
                        fontFamily: theme.fontMono, fontWeight: 700,
                        color: parseFloat(r.amount) > 0 ? C.green : C.red,
                      }}>
                        {fmt(r.amount)}{r.currency !== 'USD' ? ` ${r.currency}` : ''}
                      </td>
                      <td style={{ ...tdStyle, padding: '4px 10px' }}>
                        {!r.is_new && <span style={{ color: C.muted }}>already in feed</span>}
                        {r.is_new && r.needs_fx_review && (
                          <span style={{ color: C.yellow }} title="Stated in a foreign currency">not USD</span>
                        )}
                        {r.is_new && r.transfer_type && (
                          <span style={{ color: C.cyan }}>⇄ internal</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </>
        )}

        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
          {unmapped.length > 0 && (
            <div style={{ flex: 1, fontSize: 11, color: C.yellow, alignSelf: 'center' }}>
              Choose an entity for {unmapped.map(a => a.short_code || a.account_number).join(', ')} first.
            </div>
          )}
          <button onClick={onClose} style={btnStyle(C.muted)}>Close</button>
          <button onClick={runImport} disabled={!canImport}
            style={{ ...btnStyle(canImport ? C.green : C.border), cursor: canImport ? 'pointer' : 'default' }}>
            {busy ? 'Importing…' : preview ? `Import ${preview.new} transaction(s)` : 'Import'}
          </button>
        </div>
        </>
        )}
      </div>
    </div>
  );
}


/* ── The balances half of the Citi import ──
   The reconciliation is the reason this screen shows more than a confirmation.
   Comparing the statement balance against the transactions already staged is
   the only check that can catch a transaction which never arrived: a missing
   row is invisible among the rows, and shows up only as a gap between what
   the bank says the account holds and what the rows add up to. */
function CitiBalanceTab({ file, preview, busy, error, onPick, onImport, onClose }) {
  const breaks = preview?.reconciliation_breaks || 0;
  return (
    <>
      <label style={{
        display: 'block', padding: 20, borderRadius: 8, textAlign: 'center',
        border: `1px dashed ${C.border}`, background: C.inputBg, cursor: 'pointer', marginBottom: 16,
      }}>
        <input type="file" accept=".csv,text/csv" onChange={onPick} style={{ display: 'none' }} />
        <div style={{ fontSize: 13, fontWeight: 600, color: C.accent }}>
          {file ? file.name : 'Choose the CitiDirect Report Builder balance export'}
        </div>
        <div style={{ fontSize: 11, color: C.muted, marginTop: 4 }}>
          {busy && !preview
            ? 'Reading…'
            : 'A start-of-day balance is recorded against the day it closes — a reading dated the 27th is the close of the 26th.'}
        </div>
      </label>

      {error && (
        <div style={{
          padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 12,
          background: theme.dangerBg, border: `1px solid ${theme.dangerBorder}`, color: C.red,
        }}>{error}</div>
      )}

      {preview && (
        <>
          <div style={{
            padding: 12, borderRadius: 8, marginBottom: 14, fontSize: 12,
            background: breaks ? theme.warningBg : theme.successBg,
            border: `1px solid ${breaks ? C.yellow : C.green}`,
          }}>
            <span style={{ fontWeight: 700, color: breaks ? C.yellow : C.green }}>
              {breaks
                ? `${breaks} account(s) do not tie to the staged transactions`
                : `All ${preview.reconciled} account(s) tie to the staged transactions exactly`}
            </span>
            <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
              {breaks
                ? 'A difference means a transaction is missing from the feed, or the export starts after the account was opened.'
                : 'Every balance is fully explained by the transactions already imported — nothing is missing.'}
            </div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
            {(preview.balances || []).map(b => {
              const rec = b.reconciliation;
              const ok = rec?.ties;
              return (
                <div key={b.account_number} style={{
                  padding: 12, background: C.inputBg, borderRadius: 8,
                  border: `1px solid ${rec ? (ok ? C.green : C.yellow) : C.border}`, fontSize: 12,
                }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                    <div style={{ fontWeight: 700, fontSize: 13 }}>
                      {b.short_code}
                      <span style={{ color: C.muted, fontWeight: 500, marginLeft: 8, fontSize: 11 }}>
                        {b.entity_name}
                      </span>
                    </div>
                    <div style={{ fontFamily: theme.fontMono, fontWeight: 700, fontSize: 14 }}>
                      {fmt(b.balance)}
                    </div>
                  </div>
                  <div style={{ fontSize: 11, color: C.muted, marginTop: 3 }}>
                    Start of day {fmtDate(b.stated_date)} — recorded as the close of {fmtDate(b.as_of_date)}
                  </div>
                  {rec && (
                    <div style={{ marginTop: 6, paddingTop: 6, borderTop: `1px solid ${C.border}`, fontSize: 11 }}>
                      <div style={{ display: 'flex', gap: 16, fontFamily: theme.fontMono }}>
                        <span style={{ color: C.muted }}>
                          {rec.transaction_count} txns net{' '}
                          <span style={{ color: C.text }}>{fmt(rec.transactions_net)}</span>
                        </span>
                        <span style={{ color: ok ? C.green : C.yellow, fontWeight: 700 }}>
                          difference {fmt(rec.difference)}
                        </span>
                      </div>
                      <div style={{ color: ok ? C.muted : C.yellow, marginTop: 4 }}>
                        {rec.explanation}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {preview.unknown_accounts?.length > 0 && (
            <div style={{
              padding: 12, borderRadius: 8, marginBottom: 16, fontSize: 11,
              background: theme.warningBg, border: `1px solid ${theme.warningBorder}`,
            }}>
              <div style={{ fontWeight: 700, color: C.yellow, marginBottom: 4 }}>
                {preview.unknown_accounts.length} account(s) are not registered and will be skipped
              </div>
              {preview.unknown_accounts.map(u => (
                <div key={u.account_number} style={{ color: C.muted }}>
                  {u.short_code || u.account_number}: {u.reason}
                </div>
              ))}
            </div>
          )}
        </>
      )}

      <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
        <button onClick={onClose} style={btnStyle(C.muted)}>Close</button>
        <button onClick={onImport} disabled={!preview || busy}
          style={{ ...btnStyle(preview && !busy ? C.green : C.border),
                   cursor: preview && !busy ? 'pointer' : 'default' }}>
          {busy ? 'Recording…' : preview ? `Record ${preview.parsed} balance(s)` : 'Record balances'}
        </button>
      </div>
    </>
  );
}


/* ═══════════════════════ ALLOCATION MODAL ═══════════════════════ */
/* ── Which cash account is this? ────────────────────────────────────────
   The ledger has one cash account, `Assets:Cash`, and the entry it posts
   does not name the bank account the money moved through — the entity on
   the entry narrows it to a company, and no further. The bank row does know,
   so where a line touches cash the screen says which account that is rather
   than leaving the reader to work it out from the entity.

   That is a label, not a posting: the GL string is still `Assets:Cash`. If
   the book wants cash split per bank account, that is a chart-of-accounts
   change (`Assets:Cash:Citi:HGIO1` and friends, which `is_cash_account`
   already accepts) and every existing balance has to be split with it. */
const isCashAccount = (a) =>
  !!a && (a === 'Assets:Cash' || a.startsWith('Assets:Cash:') || a.startsWith('Cash:'));

/* What a cash account IS, in words: whose money, and which of their accounts.

   The GL string is "Assets:Cash:Mercury:Mercury Checking ••6816", which says
   nothing about whose ••6816. `cashInfo` (built on the page from Mercury's
   own account list) says it is Helena Partners Inc's un-nicknamed checking
   account — their operating account — so the screen can say "HPI · Operating
   checking ••6816" wherever that string appears. An account nicknamed in the
   portal shows the nickname: "HPI · Consultant 2 ••7573". */
const describeCash = (account, cashInfo) => {
  if (!isCashAccount(account)) return '';
  const info = cashInfo && cashInfo[account];
  const tail = account.split(':').pop();
  if (!info) {
    if (account.startsWith('Assets:Cash:Citi:')) return `Citi · ${tail}`;
    // Neither is a bank account. Tagged to a deal, the bare Assets:Cash is that
    // deal's cash at the broker (§0.11); Cash:Operating is ITG's cash on its way
    // to the bank (§0.27). "Cash, no bank account named" read as unallocated.
    if (account === 'Assets:Cash') return 'Broker cash';
    if (account === 'Cash:Operating') return 'In transit, broker to bank';
    return tail.replace(/^Mercury\s*-?\s*/i, '');
  }
  const last4 = (info.name || '').match(/(\d{4})\s*$/)?.[1] || '';
  const what = info.nickname
    ? info.nickname
    : (info.kind || '').toLowerCase() === 'savings' ? 'Savings' : 'Operating checking';
  return `${info.entity} · ${what}${last4 ? ' ••' + last4 : ''}`;
};

/* A quiet label after a cash account: the description above, if there is one. */
function CashAccountHint({ account, cashInfo }) {
  const said = describeCash(account, cashInfo);
  if (!said) return null;
  return (
    <span style={{ color: C.muted, fontSize: 11, fontWeight: 500, marginLeft: 6 }}
      title="Whose money, and which of their bank accounts. The GL string alone does not say.">
      ({said})
    </span>
  );
}

/* ── Adjusting a foreign payment to dollars ──────────────────────────────
   A Citi row for a foreign wire carries the amount in its payout currency
   and the bank's own FX rate beside it. That rate is indicative: it is what
   the bank quoted, not necessarily what it settled at, and the fees netted
   out of the payment are not in it at all. So the dollars that actually left
   the account are whatever the wire advice says, and this panel is where
   they get recorded.

   The bank's figure is never overwritten. It stays in `usd_amount` and the
   confirmed one goes to `usd_amount_override`, so a month later it is still
   possible to see what the export claimed and what somebody checked — and to
   see, from the implied rate, how far apart the two were. */
function FxAdjustPanel({ txn, readOnly, basisAmount, onChanged }) {
  const rowCcy = (txn.currency || 'USD').toUpperCase();
  const basis = postingBasis(txn);
  const adjusted = basis.source === 'reviewer';
  const bankUsd = txn.usd_amount;
  // There is no fallback any more: without this figure the row cannot be
  // posted at all, whether or not Citi gave a rate. So the panel stays open
  // and there is no Cancel to hide it behind.
  const required = !adjusted;

  // Open on anything not yet confirmed, and on an untagged row there is
  // nothing to collapse back to — the figure is required.
  const [open, setOpen] = useState(!adjusted);
  const [dollars, setDollars] = useState(
    adjusted ? Math.abs(parseFloat(txn.usd_amount_override)).toFixed(2)
             : (bankUsd ? Math.abs(parseFloat(bankUsd)).toFixed(2) : '')
  );
  const [source, setSource] = useState(txn.usd_override_source || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // A refetch, or another edit, can land while this is on screen.
  useEffect(() => {
    setDollars(
      txn.usd_amount_override != null && txn.usd_amount_override !== ''
        ? Math.abs(parseFloat(txn.usd_amount_override)).toFixed(2)
        : (txn.usd_amount ? Math.abs(parseFloat(txn.usd_amount)).toFixed(2) : '')
    );
    setSource(txn.usd_override_source || '');
  }, [txn.usd_amount_override, txn.usd_amount, txn.usd_override_source]);

  const typedRate = impliedRate(dollars, txn.amount);

  const send = async (body) => {
    setBusy(true);
    setErr('');
    try {
      const res = await api.post(
        `/reconciliation/bank-feed/${txn.mercury_txn_id}/set-usd-amount/`, body
      );
      onChanged(res.data, basisAmount);
      setOpen(false);
    } catch (e) {
      setErr(e.response?.data?.error || e.message);
    } finally {
      setBusy(false);
    }
  };

  const record = () => {
    const n = parseFloat(dollars);
    if (!n || isNaN(n) || n <= 0) { setErr('Enter the dollar amount that left the account.'); return; }
    send({ usd_amount: Math.abs(n).toFixed(4), source });
  };

  const tone = adjusted ? C.green : required ? C.red : C.yellow;

  return (
    <div style={{
      padding: '10px 16px', marginBottom: 16, borderRadius: 8,
      background: adjusted ? theme.successBg
        : required ? theme.dangerBg : theme.warningBg,
      border: `1px solid ${tone}`, fontSize: 13,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
        <div style={{ flex: 1 }}>
          <div style={{ fontWeight: 700, color: tone }}>
            {basis.source === 'reviewer'
              ? `Confirmed in dollars — booking ${fmt(txn.usd_amount_override)}`
              : `In ${rowCcy} — set the dollar amount before allocating this`}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 3, lineHeight: 1.6 }}>
            {basis.source === 'reviewer' ? (
              <>
                {fmtCcy(txn.amount, rowCcy)} at{' '}
                <b style={{ color: C.text }}>{impliedRate(txn.usd_amount_override, txn.amount)}</b>.
                The allocations below and every ledger entry posted from them are
                in dollars.
                {txn.usd_override_source
                  ? <> Source: <b style={{ color: C.text }}>{txn.usd_override_source}</b>.</>
                  : <> No source recorded for this figure.</>}
                {bankUsd && Math.abs(parseFloat(bankUsd) - parseFloat(txn.usd_amount_override)) > 0.01 && (
                  <> The bank's own rate of {txn.fx_rate} would have made it{' '}
                    {fmt(bankUsd)}, {fmt(Math.abs(parseFloat(bankUsd) - parseFloat(txn.usd_amount_override)))}{' '}
                    out.</>
                )}
              </>
            ) : bankUsd ? (
              <>The book is kept strictly in dollars, and the rate is a manual thing —
                a person has to say what this cost. Citi's export suggests{' '}
                <b style={{ color: C.text }}>{fmt(bankUsd)}</b> at {txn.fx_rate}, which is
                indicative: it is what Citi quoted, not necessarily what it settled at,
                and the fees netted out of the payment are not in it. Check it against
                the wire advice, then record it — accepting Citi's figure is fine, and
                the book will record that you did.</>
            ) : (
              <>The book is kept strictly in dollars, and the export carries no
                exchange rate, so there is nothing to suggest. This row cannot be
                allocated or posted until the dollars are recorded — put in what the
                wire advice says left the account.</>
            )}
          </div>
        </div>
        {!readOnly && !(open && required) && (
          <button onClick={() => setOpen(o => !o)} style={smallBtnStyle(tone)}>
            {open ? 'Cancel' : adjusted ? 'Change' : 'Set dollars'}
          </button>
        )}
      </div>

      {!readOnly && open && (
        <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${C.border}` }}>
          <div style={{ display: 'flex', gap: 10, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div style={{ flex: '0 0 150px' }}>
              <label style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase',
                              fontWeight: 600, display: 'block', marginBottom: 3 }}>
                Dollars that moved
              </label>
              <MF value={dollars} onChange={v => setDollars(v)} placeholder="0.00"
                style={{ ...inputStyle, textAlign: 'right', fontFamily: theme.fontMono }} />
            </div>
            <div style={{ flex: 1, minWidth: 200 }}>
              <label style={{ fontSize: 10, color: C.muted, textTransform: 'uppercase',
                              fontWeight: 600, display: 'block', marginBottom: 3 }}>
                Where it came from
              </label>
              <input type="text" value={source} onChange={e => setSource(e.target.value)}
                placeholder="e.g. wire advice, 24 Aug 2026" style={inputStyle} />
            </div>
            <button onClick={record} disabled={busy} style={{ ...btnStyle(C.green), opacity: busy ? 0.5 : 1 }}
              title="Records this figure, with a time and a source, as the dollars this payment cost.">
              {busy ? 'Saving…' : 'Record dollars'}
            </button>
            {adjusted && (
              <button onClick={() => send({ clear: true })} disabled={busy}
                style={{ ...btnStyle(C.muted), opacity: busy ? 0.5 : 1 }}
                title={`Go back to posting this row in ${rowCcy}`}>
                Undo
              </button>
            )}
          </div>
          <div style={{ fontSize: 11, color: C.muted, marginTop: 6 }}>
            {typedRate
              ? <>That is a rate of <b style={{ color: C.text }}>{typedRate}</b> on{' '}
                  {fmtCcy(txn.amount, rowCcy)}.
                  {bankUsd && <> The bank's export says {txn.fx_rate}.</>}</>
              : <>Enter the figure from the wire advice, not the export.</>}
            {' '}Allocation lines already entered are rescaled onto the new basis.
          </div>
          {err && (
            <div style={{ fontSize: 11, color: C.red, marginTop: 6, fontWeight: 600 }}>{err}</div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════ BOOK ALL AS… ═══════════════════════
   Brian, 10 September 2026, on five Railway invoices all suggested as
   Expense:Software: "lets add a feature where i can apply the same bookings
   all at once because they are all the same."

   One account, written on every selected row. The cash side is each row's own
   bank account and the direction follows the row's sign, so a run of invoices
   and a run of receipts both come out right. One line per row for the whole
   amount — anything that needs splitting still goes through Review. */
function BookAllModal({ rows, deals, entities, accountsInUse, chart, chartByValue, cashInfo, onClose, onDone }) {
  // Pre-fill from the suggester when every selected row agrees; that is the
  // case the button exists for.
  const suggested = useMemo(() => {
    const accts = new Set(rows.map(r => r.suggestion?.other_account).filter(Boolean));
    return accts.size === 1 ? Array.from(accts)[0] : '';
  }, [rows]);
  const [account, setAccount] = useState(suggested);
  const [dealId, setDealId] = useState('');
  const [entityId, setEntityId] = useState('');
  const [description, setDescription] = useState('');
  const [replace, setReplace] = useState(false);
  const [postNow, setPostNow] = useState(true);
  const [busy, setBusy] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');

  const outs = rows.filter(r => parseFloat(r.amount) < 0);
  const ins = rows.filter(r => parseFloat(r.amount) >= 0);
  const drafted = rows.filter(r => (r.allocation_count || 0) > 0);
  const total = rows.reduce((s, r) => s + Math.abs(parseFloat(r.posting_amount ?? r.amount) || 0), 0);
  const counterparties = Array.from(new Set(rows.map(r => r.counterparty_name || r.bank_description || '—')));

  const submit = async () => {
    if (!account) { setError('Pick the account the money goes to (or came from).'); return; }
    setBusy(true); setError('');
    try {
      const res = await api.post('/reconciliation/bank-feed/bulk-allocate/', {
        txn_ids: rows.map(r => r.mercury_txn_id),
        account, deal_id: dealId || null, entity_id: entityId || null,
        description, replace, post: postNow,
      });
      setResult(res.data);
    } catch (e) {
      setError(e.response?.data?.error || e.message);
    } finally { setBusy(false); }
  };

  const nameRow = (x) => `${x.counterparty || x.txn_id}${x.posted_date ? ` (${fmtDate(x.posted_date)})` : ''}`;
  const label = { fontSize: 12, fontWeight: 600, color: theme.textSecondary, marginBottom: 4 };
  const block = { marginBottom: 14 };

  if (result) {
    const skipped = result.skipped || [];
    const errors = result.errors || [];
    return (
      <Modal title="Booked" onClose={onDone} onSubmit={onDone} submitLabel="Done">
        <div style={{ fontSize: 13, color: theme.text, lineHeight: 1.6 }}>
          <div>
            Wrote <strong>{result.account}</strong> on <strong>{result.applied}</strong> of {result.requested} row{result.requested === 1 ? '' : 's'}
            {postNow ? <>, and posted <strong>{result.posted}</strong> to the General Ledger.</> : <>. Nothing is posted yet; press Post to GL when you have checked them.</>}
          </div>
          {skipped.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={label}>Left alone</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {skipped.map((sk, i) => <li key={i}>{nameRow(sk)} — {sk.reason}</li>)}
              </ul>
            </div>
          )}
          {errors.length > 0 && (
            <div style={{ marginTop: 12 }}>
              <div style={{ ...label, color: C.red }}>Allocated but not posted</div>
              <ul style={{ margin: 0, paddingLeft: 18 }}>
                {errors.map((er, i) => <li key={i}>{nameRow(er)} — {er.error}</li>)}
              </ul>
            </div>
          )}
        </div>
      </Modal>
    );
  }

  return (
    <Modal title={`Book ${rows.length} transaction${rows.length === 1 ? '' : 's'} the same way`}
      onClose={onClose} onSubmit={submit} submitDisabled={busy || !account}
      submitLabel={busy ? 'Booking…' : postNow ? 'Book and post to GL' : 'Write the bookings'} width={560}>
      <div style={{ fontSize: 13, color: theme.textSecondary, marginBottom: 14, lineHeight: 1.5 }}>
        {counterparties.length === 1
          ? <><strong style={{ color: theme.text }}>{counterparties[0]}</strong>, {rows.length} row{rows.length === 1 ? '' : 's'}</>
          : <>{counterparties.length} counterparties: {counterparties.slice(0, 4).join(', ')}{counterparties.length > 4 ? '…' : ''}</>}
        {' — '}<span style={{ fontFamily: theme.fontMono }}>{fmtCcy(total, 'USD')}</span> in all
        {outs.length && ins.length ? <> ({outs.length} out, {ins.length} in)</> : null}.
        Each row is booked for its full amount against its own bank account.
      </div>

      <div style={block}>
        <div style={label}>{ins.length && !outs.length ? 'Where the money came from' : 'Where the money went'}</div>
        <GLSelect value={account} onChange={e => setAccount(e.target.value)} style={{ ...inputStyle, width: '100%' }}
          chart={chart} byValue={chartByValue} inUse={accountsInUse} cashAccount="" cashInfo={cashInfo} />
        {suggested && account === suggested && (
          <div style={{ fontSize: 11, color: theme.textMuted, marginTop: 4 }}>Pre-filled from the suggester: every selected row suggests this account.</div>
        )}
        {!suggested && rows.some(r => r.suggestion?.other_account) && (
          <div style={{ fontSize: 11, color: C.orange, marginTop: 4 }}>The selected rows do not all suggest the same account — check this is right for all of them.</div>
        )}
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, ...block }}>
        <div>
          <div style={label}>Deal</div>
          <select value={dealId} onChange={e => setDealId(e.target.value)} style={{ ...inputStyle, width: '100%' }}>
            <option value="">— No Deal —</option>
            {deals.map(d => <option key={d.id} value={d.id}>{d.deal_name}</option>)}
          </select>
        </div>
        <div>
          <div style={label}>Whose money</div>
          <select value={entityId} onChange={e => setEntityId(e.target.value)} style={{ ...inputStyle, width: '100%' }}
            title="Leave as each row's own account owner unless the money is another Helena entity's.">
            <option value="">Each row's account owner</option>
            {(entities || []).map(en => <option key={en.id} value={en.id}>{en.short_name || en.name}</option>)}
          </select>
        </div>
      </div>

      <div style={block}>
        <div style={label}>Note on every line</div>
        <input type="text" value={description} onChange={e => setDescription(e.target.value)}
          placeholder="Optional — e.g. Railway hosting" style={{ ...inputStyle, width: '100%' }} />
      </div>

      {drafted.length > 0 && (
        <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: theme.text, marginBottom: 10 }}>
          <input type="checkbox" checked={replace} onChange={e => setReplace(e.target.checked)} />
          <span>
            Replace the draft lines already on {drafted.length} of these row{drafted.length === 1 ? '' : 's'}.
            Left unticked, those rows are skipped and named.
          </span>
        </label>
      )}
      <label style={{ display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 12, color: theme.text }}>
        <input type="checkbox" checked={postNow} onChange={e => setPostNow(e.target.checked)} />
        <span>Post to the General Ledger in the same go. Untick to write the bookings as drafts and check them first.</span>
      </label>

      {error && <div style={{ marginTop: 12, color: C.red, fontSize: 12 }}>{error}</div>}
    </Modal>
  );
}

function AllocationModal({ txn: initialTxn, deals, entities, accountsInUse, chart, chartByValue, cashInfo, onClose, onSaved, onPosted }) {
  const [txn, setTxn] = useState(initialTxn);
  const isIn = parseFloat(txn.amount) > 0;
  // What the allocation lines are stated in. On a foreign row this is the
  // row's own currency until somebody records the dollars that actually left
  // the account, and dollars from then on — the balance check, every amount
  // typed below and the currency written onto the ledger all move together.
  const basis = postingBasis(txn);
  const txnAbs = basis.amount;
  const rowCcy = (txn.currency || 'USD').toUpperCase();
  const isForeign = rowCcy !== 'USD';
  const money = (v) => fmtCcy(v, basis.currency);
  // Cash is one account per real bank account now, so the cash side of a line
  // is the account this row's money actually moved through — never the bare
  // `Assets:Cash`, which is how a Citi payment out of one entity gets credited
  // against another's cash with the entry still balancing.
  const CASH = txn.cash_account || 'Assets:Cash';
  // A foreign row nobody has put a dollar figure on cannot be allocated at
  // all: the book is dollars and the server refuses it. Say so here rather
  // than letting somebody build a split and lose it on Save.
  const untagged = !!txn.needs_usd_amount;
  const tt = txn._transferType || txn.transfer_type;
  const isTransfer = tt === 'internal_out' || tt === 'internal_in';
  // Helena Partners LLC's account only sends international wires; the money
  // is HGIO's or HPI's. The server resolves the entity from the deal or the
  // paired transfer; this screen shows the choice so nobody has to trust it.
  const isConduit = !!txn.owner_is_conduit;
  const allocGrid = '110px 1fr 1fr 1.2fr 130px 1fr 28px';
  const isPosted = txn.is_posted_to_gl;
  // A failed or pending payment is not money that moved (§0.19). The server
  // refuses to post one; the screen says so before anybody tries.
  const rowStatus = (txn.status || '').toLowerCase();
  const isUnsettled = !isPosted && ['failed', 'pending', 'dropped', 'cancelled', 'canceled', 'reversed', 'blocked'].includes(rowStatus);

  const [suggestions, setSuggestions] = useState([]);
  // The reviewer's own words about the row, and what the booking assistant
  // made of them. For when the rule-based suggestion is wrong or absent.
  const [askText, setAskText] = useState('');
  const [asking, setAsking] = useState(false);
  const [askError, setAskError] = useState('');
  const [proposal, setProposal] = useState(null);
  // The exchange so far: what the reviewer said and what came back, in order.
  // `askHistory` is the server's shape of the same thing, sent back each turn.
  const [askThread, setAskThread] = useState([]);
  const [askHistory, setAskHistory] = useState([]);
  // Pictures waiting to go with the next ask — a screenshot of the
  // distribution sheet, a wire confirmation — and whether one is being
  // dragged over the box. They go to the model as pictures, not as text.
  const [askPictures, setAskPictures] = useState([]);
  const [readingPictures, setReadingPictures] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const pictureInput = React.useRef(null);
  // The ledger legs the server would write for the lines as they stand —
  // asked of the server rather than worked out here, so the preview and the
  // posting can never disagree. Four legs, not two, when the money belongs
  // to a different entity than the account's owner.
  const [previewLegs, setPreviewLegs] = useState(null);
  const [showHelp, setShowHelp] = useState(false);

  // Fetch the freshest copy on open so we always show real allocations/ledger entries
  useEffect(() => {
    let alive = true;
    api.get(`/reconciliation/bank-feed/${initialTxn.mercury_txn_id}/`).then(r => {
      if (!alive) return;
      setTxn(prev => ({ ...prev, ...r.data }));
    }).catch(() => {});
    return () => { alive = false; };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialTxn.mercury_txn_id]);

  // What the server thinks these accounts should be. Advice only — nothing is
  // applied until the reviewer clicks Use. Not fetched for a posted row: the
  // answer is already in the ledger and suggesting an alternative would only
  // invite someone to "correct" a posting that is correct.
  useEffect(() => {
    if (initialTxn.is_posted_to_gl) return;
    let alive = true;
    api.get(`/reconciliation/bank-feed/${initialTxn.mercury_txn_id}/suggest-accounts/`)
      .then(r => { if (alive) setSuggestions(r.data.suggestions || []); })
      .catch(() => {});
    return () => { alive = false; };
  }, [initialTxn.mercury_txn_id, initialTxn.is_posted_to_gl]);

  const initAllocs = () => {
    if (txn.allocations && txn.allocations.length > 0) {
      return txn.allocations.map(a => ({
        amount: Math.abs(parseFloat(a.amount)).toFixed(2),
        debit_account: a.debit_account || '',
        credit_account: a.credit_account || '',
        deal_id: a.deal || '',
        entity_id: a.entity || txn.entity || '',
        description: a.description || '',
      }));
    }
    // An internal transfer gets Cash on the side the money actually moved,
    // and nothing on the other.
    //
    // This used to pre-fill BOTH sides as cash. That is a balanced
    // entry — it passes every check here — and it records nothing at all:
    // cash debited and cash credited for the same amount is a no-op that
    // leaves the ledger exactly as it was, while the row is marked posted.
    // Leaving the far side blank makes the reviewer name the account the
    // money went to, which is the only thing the entry is for.
    if (isTransfer) {
      // Cash to cash, both sides named, when the server knows the far
      // account (it arrives with the detail fetch and is filled in below).
      const far = txn.pair_cash_account || '';
      return [{
        amount: txnAbs.toFixed(2),
        debit_account: isIn ? CASH : far,
        credit_account: isIn ? far : CASH,
        deal_id: '',
        entity_id: txn.entity || '',
        description: `Internal transfer ${tt === 'internal_out' ? 'to' : 'from'} ${txn.counterparty_name || 'another account'}`,
      }];
    }
    return [{
      amount: txnAbs.toFixed(2),
      debit_account: isIn ? CASH : '',
      credit_account: isIn ? '' : CASH,
      deal_id: '',
      entity_id: txn.entity || '',
      description: '',
    }];
  };

  const [allocs, setAllocs]   = useState(initAllocs);
  const [notes, setNotes]     = useState(txn.review_notes || '');
  const [saving, setSaving]   = useState(false);
  const [posting, setPosting] = useState(false);
  const [unposting, setUnposting] = useState(false);

  // Re-hydrate form when the freshest txn data arrives (only if not posted)
  useEffect(() => {
    if (txn.is_posted_to_gl) return;
    if (txn.allocations && txn.allocations.length > 0) {
      setAllocs(txn.allocations.map(a => ({
        amount: Math.abs(parseFloat(a.amount)).toFixed(2),
        debit_account: a.debit_account || '',
        credit_account: a.credit_account || '',
        deal_id: a.deal || '',
        entity_id: a.entity || txn.entity || '',
        description: a.description || '',
      })));
    } else if (isTransfer && txn.pair_cash_account) {
      // The far side of the transfer arrives with the detail fetch; fill the
      // blank side of a line nobody has touched.
      const far = txn.pair_cash_account;
      setAllocs(prev => prev.map(a => (
        a.debit_account && a.credit_account ? a
          : { ...a, debit_account: a.debit_account || (isIn ? CASH : far), credit_account: a.credit_account || (isIn ? far : CASH) }
      )));
    }
    setNotes(txn.review_notes || '');
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [txn.allocations, txn.review_notes, txn.is_posted_to_gl]);

  const allocTotal = useMemo(() => allocs.reduce((s, a) => s + (parseFloat(a.amount) || 0), 0), [allocs]);
  const remaining = txnAbs - allocTotal;
  const isBalanced = Math.abs(remaining) < 0.01;

  // Adjusting a foreign row to dollars changes what every line below is
  // denominated in. The server rescales the lines it has saved; these are the
  // ones it has not seen yet, and leaving them in euros would leave the screen
  // showing a split that cannot balance and never say why.
  const rebaseLocalAllocs = useCallback((fromBasis, toBasis) => {
    if (!fromBasis || !toBasis || fromBasis === toBasis) return;
    setAllocs(prev => {
      const total = prev.reduce((sum, a) => sum + (parseFloat(a.amount) || 0), 0);
      // A part-built split is somebody mid-thought; rescaling it would invent
      // figures they never typed.
      if (Math.abs(total - fromBasis) > 0.01) return prev;
      let running = 0;
      return prev.map((a, i) => {
        if (i === prev.length - 1) {
          return { ...a, amount: (toBasis - running).toFixed(2) };
        }
        const scaled = Math.round((parseFloat(a.amount) || 0) * toBasis / fromBasis * 100) / 100;
        running += scaled;
        return { ...a, amount: scaled.toFixed(2) };
      });
    });
  }, []);

  // The reviewer has recorded (or withdrawn) a dollar figure. The response
  // carries the whole row back, allocations included, so the screen takes the
  // server's answer rather than predicting it.
  const handleFxChanged = useCallback((data, oldBasis) => {
    const fresh = data.transaction || {};
    setTxn(prev => ({ ...prev, ...fresh }));
    if (!data.allocations_rescaled) {
      rebaseLocalAllocs(oldBasis, postingBasis({ ...txn, ...fresh }).amount);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rebaseLocalAllocs, txn]);

  // Debiting and crediting the same account posts two entries that cancel
  // exactly. It balances, so nothing else here catches it, and it books
  // nothing while marking the transaction done. It is never what anyone meant.
  const selfCancelling = allocs.filter(
    a => a.debit_account && a.debit_account === a.credit_account
  );
  const allValid =
    allocs.every(a => a.amount && a.debit_account && a.credit_account && parseFloat(a.amount) > 0)
    && selfCancelling.length === 0
    // The book is dollars. A foreign row with no dollar figure on it has
    // nothing valid to allocate, whatever the lines say.
    && !untagged;

  const applySuggestion = (sg) => {
    setAllocs(prev => {
      const next = [...prev];
      // Fill the first line that is incomplete or self-cancelling; failing
      // that, the first line.
      const i = next.findIndex(a => !a.debit_account || !a.credit_account
        || a.debit_account === a.credit_account);
      const target = i === -1 ? 0 : i;
      next[target] = {
        ...next[target],
        debit_account: sg.debit_account || next[target].debit_account,
        credit_account: sg.credit_account || next[target].credit_account,
        deal_id: sg.deal_id != null ? String(sg.deal_id) : next[target].deal_id,
        amount: next[target].amount || txnAbs.toFixed(2),
      };
      return next;
    });
  };

  const addPictures = async (files) => {
    const list = Array.from(files || []);
    if (!list.length) return;
    const room = MAX_PER_TURN - askPictures.length;
    if (room <= 0) { setAskError(`At most ${MAX_PER_TURN} pictures at a time.`); return; }
    setReadingPictures(true); setAskError('');
    const ready = [];
    const problems = [];
    for (const [i, f] of list.slice(0, room).entries()) {
      try { ready.push(await preparePicture(f, askPictures.length + i + 1)); }
      catch (e) { problems.push(e.message); }
    }
    if (list.length > room) problems.push(`Only ${MAX_PER_TURN} pictures go at a time; the rest were left out.`);
    setAskPictures(prev => [...prev, ...ready].slice(0, MAX_PER_TURN));
    if (problems.length) setAskError(problems.join(' '));
    setReadingPictures(false);
  };

  const onPastePictures = (e) => {
    const files = pictureFiles(e.clipboardData);
    if (!files.length) return;   // plain text pastes as text
    e.preventDefault();
    addPictures(files);
  };

  const onDropPictures = (e) => {
    e.preventDefault();
    setDragOver(false);
    addPictures(pictureFiles(e.dataTransfer));
  };

  const askAssistant = async () => {
    const context = askText.trim();
    if ((!context && !askPictures.length) || readingPictures) return;
    setAsking(true); setAskError('');
    try {
      // Every turn carries the whole exchange and what the grid holds now, so
      // an answer to its question refines the last proposal rather than
      // starting over, and a line edited by hand is respected. Pictures
      // already sent are inside `history`; `images` are this turn's.
      const r = await api.post(`/reconciliation/bank-feed/${txn.mercury_txn_id}/ask/`, {
        context, history: askHistory,
        images: askPictures.map(p => ({ name: p.name, media_type: p.media_type, data: p.data })),
        current_lines: allocs.filter(a => a.debit_account || a.credit_account).map(a => ({
          amount: a.amount, debit_account: a.debit_account, credit_account: a.credit_account,
          deal_id: a.deal_id || null, entity_id: a.entity_id || null, description: a.description,
        })),
      });
      setAskThread(prev => [...prev, { you: context, pictures: askPictures, proposal: r.data }]);
      setAskHistory(r.data.history || []);
      setProposal(r.data);
      setAskText('');
      if (askPictures.length) rememberPictures(askPictures);
      setAskPictures([]);
    } catch (e) {
      setAskError(e.response?.data?.error || e.message);
    } finally { setAsking(false); }
  };

  useEffect(() => {
    if (isPosted) return undefined;
    const ready = allocs.filter(a => a.debit_account && a.credit_account && parseFloat(a.amount) > 0);
    if (!ready.length) { setPreviewLegs(null); return undefined; }
    let alive = true;
    const t = setTimeout(() => {
      api.post(`/reconciliation/bank-feed/${txn.mercury_txn_id}/preview-entries/`, {
        allocations: ready.map(a => ({
          amount: a.amount, debit_account: a.debit_account, credit_account: a.credit_account,
          deal_id: a.deal_id || null, entity_id: a.entity_id || null, description: a.description,
        })),
      }).then(r => { if (alive) setPreviewLegs(r.data.legs || []); })
        .catch(() => { if (alive) setPreviewLegs(null); });
    }, 350);
    return () => { alive = false; clearTimeout(t); };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [JSON.stringify(allocs), isPosted]);

  const restartAsk = () => {
    setAskThread([]); setAskHistory([]); setProposal(null); setAskText(''); setAskError(''); setAskPictures([]);
  };
  // The picture sent from the last row, offered on this one: one sheet pays
  // half a dozen people.
  const lastPictures = recentPictures();

  // The proposal becomes the allocation lines, and what the reviewer said
  // becomes the review note — so a month from now the entry says why.
  const applyProposal = (p) => {
    if (!p || !p.lines || !p.lines.length) return;
    setAllocs(p.lines.map(l => ({
      amount: (parseFloat(l.amount) || 0).toFixed(2),
      debit_account: l.debit_account || '',
      credit_account: l.credit_account || '',
      deal_id: l.deal_id != null ? String(l.deal_id) : '',
      entity_id: l.entity_id != null ? String(l.entity_id) : (txn.entity || ''),
      description: l.description || '',
    })));
    const said = [...askThread.map(t => t.you)].filter(Boolean);
    const pics = askThread.flatMap(t => (t.pictures || []).map(pic => pic.name));
    const note = [
      said.length ? 'Context: ' + said.join(' / ') : '',
      pics.length ? 'From ' + (pics.length === 1 ? 'the picture ' : 'the pictures ') + pics.join(', ') : '',
    ].filter(Boolean).join(' — ');
    if (note) setNotes(prev => (prev && prev.includes(note)) ? prev : (prev ? prev + '\n' : '') + note);
  };

  const updateAlloc = (idx, field, value) => {
    setAllocs(prev => prev.map((a, i) => i === idx ? { ...a, [field]: value } : a));
  };

  const addLine = () => {
    setAllocs(prev => [...prev, {
      amount: remaining > 0.01 ? remaining.toFixed(2) : '',
      debit_account: isIn ? CASH : '',
      credit_account: isIn ? '' : CASH,
      deal_id: '', entity_id: txn.entity || '', description: '',
    }]);
  };

  const removeLine = (idx) => {
    if (allocs.length <= 1) return;
    setAllocs(prev => prev.filter((_, i) => i !== idx));
  };

  const handleSave = async () => {
    if (!allValid || !isBalanced) return;
    setSaving(true);
    try {
      await api.post(`/reconciliation/bank-feed/${txn.mercury_txn_id}/save-allocations/`, {
        review_notes: notes,
        allocations: allocs.map(a => ({
          amount: a.amount, debit_account: a.debit_account, credit_account: a.credit_account,
          deal_id: a.deal_id || null, entity_id: a.entity_id || null, description: a.description,
        })),
      });
      onSaved();
    } catch (e) { alert('Save failed: ' + (e.response?.data?.error || e.message)); }
    finally { setSaving(false); }
  };

  const handlePost = async () => {
    if (!allValid || !isBalanced) return;
    // Posting a foreign row without adjusting it is allowed — the ledger
    // records the currency, so nothing is misstated — but it has to be a
    // decision rather than the thing that happens when nobody reads the
    // banner. So the confirmation says which currency is about to be booked.
    // Every posting from here is in dollars: an untagged foreign row cannot
    // reach this button, and a tagged one books at its dollar figure. So the
    // confirmation names the foreign wire behind it instead of warning about
    // a currency that is no longer going to be posted.
    const wireNote = isForeign
      ? `\n\nThis is ${fmtCcy(txn.amount, rowCcy)} booked at ${money(txnAbs)}` +
        `${txn.usd_override_source ? ` — ${txn.usd_override_source}` : ''}.`
      : '';
    if (!window.confirm(
      `Save allocations and post ${money(txnAbs)} to the General Ledger? ` +
      `This cannot be undone.${wireNote}`
    )) return;
    setPosting(true);
    try {
      await api.post(`/reconciliation/bank-feed/${txn.mercury_txn_id}/save-allocations/`, {
        review_notes: notes,
        allocations: allocs.map(a => ({
          amount: a.amount, debit_account: a.debit_account, credit_account: a.credit_account,
          deal_id: a.deal_id || null, entity_id: a.entity_id || null, description: a.description,
        })),
      });
      const res = await api.post(`/reconciliation/bank-feed/${txn.mercury_txn_id}/post-to-gl/`);
      alert(`Posted ${res.data.allocations_posted} allocation(s) to GL.`);
      onPosted();
    } catch (e) { alert('Post failed: ' + (e.response?.data?.error || e.message)); }
    finally { setPosting(false); }
  };

  const handleUnpost = async () => {
    if (!window.confirm(
      'Unpost this transaction?\n\n' +
      'A reversing entry dated TODAY will be added to the GL ' +
      '(originals stay in their period). You can then re-edit allocations.'
    )) return;
    setUnposting(true);
    try {
      const res = await api.post(`/reconciliation/bank-feed/${txn.mercury_txn_id}/unpost/`);
      alert(`Reversed ${res.data.entries_reversed} ledger entry(ies). Transaction is now unposted.`);
      onPosted();
    } catch (e) { alert('Unpost failed: ' + (e.response?.data?.error || e.message)); }
    finally { setUnposting(false); }
  };

  const allocationsForDisplay = txn.allocations || [];
  const ledgerEntries = txn.posted_ledger_entries || [];
  const dealLookup = (id) => deals.find(d => d.id === parseInt(id))?.deal_name || '';

  return (
    <div style={overlayStyle} onClick={onClose}>
      <div style={{ ...modalStyle, width: 'min(1180px, 96vw)' }} onClick={e => e.stopPropagation()}>
        {/* Header */}
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, fontSize: 18, fontWeight: 700 }}>Review & Allocate</h2>
            <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>
              Categorize this transaction — assign it to accounts and deals
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', color: C.muted, fontSize: 18, cursor: 'pointer' }}>✕</button>
        </div>

        {/* Transfer banner */}
        {isConduit && (
          <div style={{
            padding: '10px 14px', borderRadius: 8, marginBottom: 14,
            background: theme.warningBg, border: `1px solid ${theme.warningBorder}`, fontSize: 12,
          }}>
            <div style={{ fontWeight: 700, color: theme.warning }}>
              {txn.entity_name || 'This account'} only sends wires for the group
            </div>
            <div style={{ color: C.muted, fontSize: 11 }}>
              The account is the LLC's, the money is not. Each line below says whose it is: a line with a
              deal starts at the deal's entity, and "Whose money" changes it — a cost the deal never bore,
              like due diligence on a deal that did not close, is the management company's. The ledger
              entry is written to the entity shown there, with the cash still leaving this account.
            </div>
          </div>
        )}
        {isTransfer && (
          <div style={{
            padding: '10px 16px', marginBottom: 16, borderRadius: 8,
            background: theme.infoBg, border: `1px solid ${theme.infoBorder}`,
            display: 'flex', alignItems: 'center', gap: 10, fontSize: 13,
          }}>
            <span style={{ fontSize: 18 }}>⇄</span>
            <div>
              <div style={{ fontWeight: 700, color: theme.info }}>Transfer between our own accounts</div>
              <div style={{ fontSize: 11, color: C.muted }}>
                Not money in or out of the group — cash leaving one of our accounts and arriving in another.
                It is booked once, cash to cash{txn.pair_cash_account ? <>: <code style={{ fontFamily: theme.fontMono }}>{txn.pair_cash_account}</code> on the far side</> : ''}.
                {txn.matched_transfer_id
                  ? ' Post this leg and the other leg is marked posted with it; posting both would count the money twice.'
                  : ' The other leg has not been matched, so post one leg only.'}
              </div>
            </div>
          </div>
        )}

        {/* Transaction summary */}
        <div style={{
          display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12, padding: 16,
          background: C.inputBg, borderRadius: 8, marginBottom: 20, border: `1px solid ${C.border}`,
        }}>
          <InfoField label="Date" value={fmtDate(txn.posted_date || txn.created_date)} />
          <InfoField label="Amount" value={fmtCcy(txn.amount, rowCcy)} color={isTransfer ? C.cyan : isIn ? C.green : C.red} />
          <InfoField label="Direction" value={isIn ? 'Inflow (Money In)' : 'Outflow (Money Out)'} color={isIn ? C.green : C.red} />
          {/* Which real account the money moved through. The GL says
              "Assets:Cash" and nothing more, so this row is the only place
              the bank account is named — see the note by the Cash line in
              the ledger preview. */}
          <InfoField
            label="Bank account"
            value={[describeCash(CASH, cashInfo),
                    `${sourceName(txn.source)} ${(txn.mercury_account_name || txn.mercury_account_id || 'unnamed account').replace(/^Mercury\s*-?\s*/i, '')}`]
              .filter(Boolean).join(' — ')}
            span={2}
          />
          {basis.adjusted && (
            <InfoField label="Posting amount" value={fmt(txn.usd_amount_override)} color={C.green} />
          )}
          <InfoField label="Counterparty"
            value={(() => {
              const cp = txn.counterparty_name || '—';
              // A transfer's counterparty is one of our own accounts; say whose.
              const said = isTransfer && /^Mercury/i.test(cp)
                ? describeCash(`Assets:Cash:Mercury:${cp.replace(/^Mercury\s*-\s*/i, 'Mercury ')}`, cashInfo) : '';
              return said ? `${cp} (${said})` : cp;
            })()} span={2} />
          <InfoField label="Status" value={txn.status} />
          {txn.bank_description && <InfoField label="Bank Description" value={txn.bank_description} span={2} />}
          {txn.mercury_category && <InfoField label="Category" value={txn.mercury_category} />}
        </div>

        {/* Non-USD panel. The export states a foreign payment in its payout
            currency, and the rate beside it is the bank's indicative one — so
            the dollar figure derived from it is a guess with a decimal point
            on it. This is where the reviewer puts the real number off the
            wire advice, and the whole posting moves onto it. */}
        {isForeign && (
          <FxAdjustPanel
            txn={txn}
            readOnly={isPosted}
            basisAmount={txnAbs}
            onChanged={handleFxChanged}
          />
        )}

        {isUnsettled && (
          <div style={{
            padding: '12px 16px', marginBottom: 16, borderRadius: 8,
            background: theme.dangerBg, border: `1px solid ${theme.dangerBorder}`,
            color: C.red, fontSize: 13, fontWeight: 600,
          }}>
            {rowStatus === 'failed'
              ? 'Mercury reports this payment as failed. No money moved, so it cannot be posted to the GL. The retry, if there was one, is its own row. Skip this one.'
              : rowStatus === 'pending'
                ? 'Mercury reports this payment as pending. It can be posted once it settles; the next sync updates the status.'
                : rowStatus === 'dropped'
                  ? 'Mercury no longer lists this pending payment: it was released before it settled, as a card hold is. No money moved, so there is nothing to book.'
                  : `Mercury reports this payment as ${rowStatus}. No money moved, so it cannot be posted to the GL.`}
          </div>
        )}

        {/* Posted-status banner */}
        {isPosted && (
          <div style={{
            padding: '12px 16px', marginBottom: 16, borderRadius: 8,
            background: theme.successBg, border: `1px solid ${theme.successBorder}`,
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 12 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, color: C.green, fontSize: 13 }}>
                  ✓ Posted to GL
                  {txn.posted_at && (
                    <span style={{ fontSize: 11, color: C.muted, fontWeight: 500, marginLeft: 8 }}>
                      on {fmtDate(txn.posted_at.substring(0, 10))}
                    </span>
                  )}
                  {txn.linked_ledger_txn_id && (
                    <span style={{ fontSize: 11, color: C.muted, fontWeight: 500, marginLeft: 8, fontFamily: theme.fontMono }}>
                      txn #{txn.linked_ledger_txn_id}
                    </span>
                  )}
                </div>
                {txn.posted_description && (
                  <div style={{ fontSize: 12, color: C.muted, marginTop: 4 }}>{txn.posted_description}</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── Suggested accounts ──
            The answer to "we are not accountants and do not know what to
            pick". Advice only: nothing is applied until Use is clicked. */}
        {!isPosted && (
          <div style={{ marginBottom: 16 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
              <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
                Suggested accounts
                <span style={{ fontSize: 11, color: C.muted, fontWeight: 500, marginLeft: 8 }}>
                  a starting point — check it before you post
                </span>
              </h3>
              <button onClick={() => setShowHelp(h => !h)} style={{
                background: 'none', border: `1px solid ${C.border}`, borderRadius: 6,
                color: C.accent, fontSize: 11, fontWeight: 600, cursor: 'pointer', padding: '4px 10px',
              }}>{showHelp ? 'Hide' : 'How do I choose?'}</button>
            </div>

            {showHelp && (
              <div style={{
                padding: 14, marginBottom: 10, borderRadius: 8, background: C.inputBg,
                border: `1px solid ${C.border}`, fontSize: 12, lineHeight: 1.65, color: C.text,
              }}>
                <div style={{ fontWeight: 700, marginBottom: 6 }}>Every bank row has a cash side and an other side.</div>
                <div style={{ color: C.muted, marginBottom: 10 }}>
                  The cash side is automatic: money in means <b style={{ color: C.text }}>Debit Cash</b>,
                  money out means <b style={{ color: C.text }}>Credit Cash</b>. The only real question is
                  the other side, and it is always one of four things.
                </div>
                {[
                  ['We bought something we still own', 'Investment account (Notes, Preferred, Stock, Warrants, ELOC)',
                   'Money left, but we are no poorer — we swapped cash for an asset.'],
                  ['We earned it', 'Income (Interest, Dividend, Realized Gain)',
                   'Money arrived and we are genuinely richer.'],
                  ['We consumed it', 'Expense (Legal, Audit, Admin, Software…)',
                   'Money left and there is nothing to show for it. We are poorer.'],
                  ['It is still ours, elsewhere', 'Due From / Due To another Helena entity',
                   'Money moved inside the group. The group is neither richer nor poorer.'],
                ].map(([q, acct, note]) => (
                  <div key={q} style={{ display: 'flex', gap: 10, padding: '5px 0', borderTop: `1px solid ${C.border}` }}>
                    <div style={{ flex: '0 0 190px', fontWeight: 600 }}>{q}</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ color: C.accent, fontWeight: 600 }}>{acct}</div>
                      <div style={{ color: C.muted, fontSize: 11 }}>{note}</div>
                    </div>
                  </div>
                ))}
                <div style={{ marginTop: 10, paddingTop: 8, borderTop: `1px solid ${C.border}`, color: C.yellow, fontSize: 11 }}>
                  If the same account appears on both sides, the entry cancels itself out and records
                  nothing. That is never right.
                </div>
              </div>
            )}

            {suggestions.length === 0 && (
              <div style={{ padding: '10px 12px', borderRadius: 8, background: C.inputBg, border: `1px solid ${C.border}`,
                            fontSize: 12, color: C.muted }}>
                Nothing on this row is recognised — no counterparty, deal or wording the book knows. Say what it was below.
              </div>
            )}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {suggestions.map((sg, i) => {
                const tone = { high: C.green, medium: C.accent, low: C.yellow, none: C.muted }[sg.confidence] || C.muted;
                const certainty = CERTAINTY[sg.confidence] || '';
                return (
                  <div key={i} style={{
                    padding: 12, background: C.inputBg, borderRadius: 8,
                    border: `1px solid ${i === 0 ? tone : C.border}`, fontSize: 12,
                  }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>{sg.label}</span>
                          <span style={{
                            padding: '1px 7px', borderRadius: 3, fontSize: 9, fontWeight: 700,
                            background: `${tone}22`, color: tone, textTransform: 'uppercase',
                          }}>{certainty}</span>
                        </div>
                        <div style={{ color: C.text, marginBottom: 5 }}>{sg.plain}</div>
                        <div style={{ fontFamily: theme.fontMono, fontSize: 11, color: C.muted, marginBottom: 4 }}>
                          DR {sg.debit_account || '— you choose —'}
                          {describeCash(sg.debit_account, cashInfo) && <span style={{ fontFamily: theme.fontBody }}> ({describeCash(sg.debit_account, cashInfo)})</span>}
                          {'  /  '}
                          CR {sg.credit_account || '— you choose —'}
                          {describeCash(sg.credit_account, cashInfo) && <span style={{ fontFamily: theme.fontBody }}> ({describeCash(sg.credit_account, cashInfo)})</span>}
                        </div>
                        <div style={{ color: C.muted, fontSize: 11, fontStyle: 'italic' }}>{sg.why}</div>
                        {sg.needs_input && (
                          <div style={{
                            marginTop: 6, padding: '6px 9px', borderRadius: 5,
                            background: theme.warningBg, color: C.yellow, fontSize: 11,
                          }}>⚠ {sg.needs_input}</div>
                        )}
                      </div>
                      {(sg.debit_account || sg.credit_account) && (
                        <button onClick={() => applySuggestion(sg)} style={smallBtnStyle(tone)}>Use</button>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Tell it what this was — in words, pictures, or both */}
            <div
              onPaste={onPastePictures}
              onDragOver={e => { if (Array.from(e.dataTransfer?.types || []).includes('Files')) { e.preventDefault(); setDragOver(true); } }}
              onDragLeave={e => { if (!e.currentTarget.contains(e.relatedTarget)) setDragOver(false); }}
              onDrop={onDropPictures}
              style={{
                marginTop: 10, padding: 12, borderRadius: 8,
                background: theme.accentLight, fontSize: 12,
                border: dragOver ? `2px solid ${C.accent}` : `1px dashed ${theme.accentBorder}`,
              }}>
              <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 2 }}>Tell it what this was</div>
              <div style={{ color: C.muted, fontSize: 11, marginBottom: 8 }}>
                When the suggestion is wrong or there is none. A sentence or two — who, what for, which deal — or
                paste a picture: the distribution sheet, a wire confirmation, an invoice. It reads the picture and
                proposes the entry. You still check it before posting; nothing is written until you do.
              </div>
              {askThread.map((t, i) => (
                <div key={i} style={{ marginBottom: 8 }}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'flex-start', marginBottom: 6 }}>
                    <span style={{ flex: '0 0 auto', fontSize: 10, fontWeight: 700, color: C.muted, textTransform: 'uppercase',
                                   letterSpacing: '0.04em', paddingTop: 3 }}>You</span>
                    <div style={{ fontSize: 12, color: C.text, background: C.card, border: `1px solid ${C.border}`,
                                  borderRadius: 8, padding: '6px 10px', flex: 1 }}>
                      {t.you || <span style={{ color: C.muted }}>(a picture, nothing typed)</span>}
                      {(t.pictures || []).length > 0 && <PictureStrip pictures={t.pictures} />}
                    </div>
                  </div>
                  {i < askThread.length - 1 && (
                    <div style={{ fontSize: 11, color: C.muted, paddingLeft: 34, marginBottom: 4 }}>
                      Proposed {CERTAINTY[t.proposal.confidence] ? CERTAINTY[t.proposal.confidence].toLowerCase() : ''}:{' '}
                      {(t.proposal.lines || []).map(l => `${money(l.amount)} ${l.debit_account} / ${l.credit_account}`).join('; ')}
                      {(t.proposal.questions || []).length ? ` — asked ${t.proposal.questions.length} question${t.proposal.questions.length > 1 ? 's' : ''}` : ''}
                    </div>
                  )}
                </div>
              ))}
              <textarea value={askText} onChange={e => setAskText(e.target.value)} rows={2}
                placeholder={askThread.length
                  ? 'Answer its questions, add detail, or say what to change…'
                  : 'e.g. Jeremy\'s share of the 24 September distribution — or just paste the sheet (Ctrl+V)'}
                style={{ ...inputStyle, resize: 'vertical', fontFamily: 'inherit', marginBottom: 8 }}
                onKeyDown={e => { if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) askAssistant(); }} />
              {askPictures.length > 0 && (
                <PictureStrip pictures={askPictures} onRemove={i => setAskPictures(prev => prev.filter((_, j) => j !== i))} />
              )}
              <input ref={pictureInput} type="file" accept="image/png,image/jpeg,image/gif,image/webp" multiple
                style={{ display: 'none' }}
                onChange={e => { addPictures(e.target.files); e.target.value = ''; }} />
              <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                <Button size="sm" onClick={askAssistant}
                  disabled={asking || readingPictures || (!askText.trim() && !askPictures.length)}>
                  {asking ? (askPictures.length ? 'Reading the picture…' : 'Thinking…')
                    : askThread.length ? 'Reply and refine' : 'Propose the entry'}
                </Button>
                <Button size="sm" variant="outline" onClick={() => pictureInput.current && pictureInput.current.click()}
                  disabled={asking || readingPictures || askPictures.length >= MAX_PER_TURN}
                  title="Or paste a screenshot straight into the box, or drag a picture onto it.">
                  {readingPictures ? 'Preparing picture…' : 'Attach picture'}
                </Button>
                {!askThread.length && !askPictures.length && lastPictures.length > 0 && !asking && (
                  <Button size="sm" variant="ghost" onClick={() => setAskPictures(lastPictures)}
                    title="The picture you sent from the last row you reviewed — one sheet usually pays several people.">
                    Use the last picture ({lastPictures.map(pic => pic.name).join(', ')})
                  </Button>
                )}
                {askThread.length > 0 && !asking && (
                  <Button size="sm" variant="ghost" onClick={restartAsk}>Start over</Button>
                )}
                {askError && <span style={{ color: C.red, fontSize: 11 }}>{askError}</span>}
                {askThread.length > 0 && !asking && (
                  <span style={{ fontSize: 11, color: C.muted, marginLeft: 'auto' }}>
                    Each reply refines the proposal below; it also sees any lines you edit by hand.
                  </span>
                )}
              </div>

              {proposal && (() => {
                const tone = { high: C.green, medium: C.accent, low: C.yellow }[proposal.confidence] || C.muted;
                const dealName = (id) => id != null ? (deals.find(d => String(d.id) === String(id))?.deal_name || `deal ${id}`) : '';
                const entityName = (id) => id != null ? ((entities || []).find(en => String(en.id) === String(id))?.short_name || '') : '';
                return (
                  <div style={{ marginTop: 10, padding: 12, background: C.card, borderRadius: 8, border: `1px solid ${tone}` }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                      <div style={{ flex: 1 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 4 }}>
                          <span style={{ fontWeight: 700, fontSize: 13 }}>
                            {askThread.some(t => (t.pictures || []).length) ? 'Proposed from what you said and showed' : 'Proposed from what you said'}
                          </span>
                          <span style={{ padding: '1px 7px', borderRadius: 3, fontSize: 9, fontWeight: 700,
                                         background: `${tone}22`, color: tone, textTransform: 'uppercase' }}>
                            {CERTAINTY[proposal.confidence] || proposal.confidence}
                          </span>
                        </div>
                        <div style={{ color: C.text, marginBottom: 6 }}>{proposal.explanation}</div>
                        {proposal.model && (
                          <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}
                            title="The model the API reports answered this, not the one the server asked for.">
                            Answered by {modelLabel(proposal.model)}{proposal.effort ? `, ${proposal.effort} effort` : ''}
                          </div>
                        )}
                        {(proposal.looked_up || []).length > 0 && (
                          <div style={{ fontSize: 10, color: C.muted, marginBottom: 6 }}
                            title="What it read in the book before answering: the general ledger, the bank feed, the deals and the brokers' rows, as they stand now.">
                            <span style={{ fontWeight: 600 }}>It read in the book:</span>
                            {proposal.looked_up.map((s, i) => <div key={i}>• {s}</div>)}
                          </div>
                        )}
                        {(proposal.lines || []).map((l, i) => (
                          <div key={i} style={{ fontFamily: theme.fontMono, fontSize: 11, color: C.muted, marginBottom: 3 }}>
                            {money(l.amount)}  DR {l.debit_account}
                            {describeCash(l.debit_account, cashInfo) && <span style={{ fontFamily: theme.fontBody }}> ({describeCash(l.debit_account, cashInfo)})</span>}
                            {'  /  '}CR {l.credit_account}
                            {describeCash(l.credit_account, cashInfo) && <span style={{ fontFamily: theme.fontBody }}> ({describeCash(l.credit_account, cashInfo)})</span>}
                            {(l.deal_id != null || l.entity_id != null) && (
                              <span style={{ fontFamily: theme.fontBody, color: C.purple }}>
                                {'  '}{[dealName(l.deal_id), entityName(l.entity_id)].filter(Boolean).join(' · ')}
                              </span>
                            )}
                          </div>
                        ))}
                        {(proposal.warnings || []).map((w, i) => (
                          <div key={i} style={{ marginTop: 6, padding: '6px 9px', borderRadius: 5,
                                                background: theme.warningBg, color: C.yellow, fontSize: 11 }}>⚠ {w}</div>
                        ))}
                        {(proposal.questions || []).length > 0 && (
                          <div style={{ marginTop: 6, fontSize: 11, color: C.muted }}>
                            <div style={{ fontWeight: 600 }}>It would want to know:</div>
                            {proposal.questions.map((q, i) => <div key={i}>• {q}</div>)}
                          </div>
                        )}
                      </div>
                      {(proposal.lines || []).length > 0 && proposal.usable !== false ? (
                        <button onClick={() => applyProposal(proposal)} style={smallBtnStyle(tone)}>Use</button>
                      ) : (
                        <span style={{ fontSize: 11, color: C.muted, maxWidth: 150, textAlign: 'right' }}
                          title="A proposal that does not tie to the cash, or that books something other than this row's cash movement, is shown for reading but cannot be applied.">
                          Not applied as-is — see the warnings, then tell it more or fix the lines by hand.
                        </span>
                      )}
                    </div>
                  </div>
                );
              })()}
            </div>
          </div>
        )}

        {/* Allocation lines */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 10 }}>
            <h3 style={{ margin: 0, fontSize: 14, fontWeight: 600 }}>
              {isPosted ? 'Posted Allocations' : 'GL Allocations'}
            </h3>
            {!isPosted && (
              <div style={{ fontSize: 12 }}>
                <span style={{ color: C.muted }}>Allocated: </span>
                <span style={{ fontWeight: 700, color: isBalanced ? C.green : C.yellow }}>{money(allocTotal)}</span>
                <span style={{ color: C.muted }}> / {money(txnAbs)}</span>
                {!isBalanced && (
                  <span style={{ color: C.red, marginLeft: 8, fontWeight: 600 }}>Remaining: {money(remaining)}</span>
                )}
                {selfCancelling.length > 0 && (
                  <span style={{ color: C.red, marginLeft: 8, fontWeight: 600 }}
                    title="Debiting and crediting one account cancels out and records nothing.">
                    ⚠ Same account on both sides
                  </span>
                )}
              </div>
            )}
          </div>

          {isPosted ? (
            allocationsForDisplay.length === 0 ? (
              <div style={{ fontSize: 12, color: C.muted, fontStyle: 'italic', padding: '8px 0' }}>
                No allocation detail recorded — see ledger entries below.
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                {allocationsForDisplay.map((a, idx) => {
                  const dealName = a.deal_name || (a.deal ? dealLookup(a.deal) : '');
                  const fromCS = a.source === 'capital_source';
                  return (
                    <div key={a.id || idx} style={{
                      padding: 12, background: C.inputBg, borderRadius: 6,
                      border: `1px solid ${C.border}`, fontSize: 12,
                    }}>
                      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 6 }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span style={{ fontWeight: 700, fontSize: 13, fontFamily: theme.fontMono }}>
                            {money(a.amount)}
                          </span>
                          {dealName && (
                            <span style={pill(C.purple)}>
                              {dealName}
                            </span>
                          )}
                          {fromCS && (
                            <span style={{ ...pill(C.accent), fontSize: 10 }}
                              title="Posted via Capital Sources panel">
                              CAPITAL SOURCE
                            </span>
                          )}
                        </div>
                      </div>
                      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, fontSize: 11 }}>
                        <div>
                          <span style={{ color: C.muted, fontWeight: 600, textTransform: 'uppercase', fontSize: 9 }}>Debit — what we got</span>
                          <div style={{ fontWeight: 600 }}>
                            {a.debit_account}<CashAccountHint account={a.debit_account} cashInfo={cashInfo} />
                          </div>
                        </div>
                        <div>
                          <span style={{ color: C.muted, fontWeight: 600, textTransform: 'uppercase', fontSize: 9 }}>Credit — where it came from</span>
                          <div style={{ fontWeight: 600 }}>
                            {a.credit_account}<CashAccountHint account={a.credit_account} cashInfo={cashInfo} />
                          </div>
                        </div>
                      </div>
                      {a.description && (
                        <div style={{ marginTop: 6, color: C.muted, fontStyle: 'italic' }}>{a.description}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )
          ) : (
            <>
              <div style={{
                display: 'grid', gridTemplateColumns: allocGrid, gap: 6,
                padding: '6px 0', borderBottom: `1px solid ${C.border}`, marginBottom: 6,
              }}>
                <div style={colHeaderStyle} title={`Every line is in ${basis.currency}`}>
                  Amount{basis.currency !== 'USD' ? ` (${basis.currency})` : ''}
                </div>
                <div style={colHeaderStyle} title="The account that GAINS value. For money arriving in the bank, this is Cash.">
                  Debit — what we got
                </div>
                <div style={colHeaderStyle} title="The account that GIVES UP value. For money leaving the bank, this is Cash.">
                  Credit — where it came from
                </div>
                <div style={colHeaderStyle}>Deal</div>
                <div style={colHeaderStyle}
                  title={isConduit
                    ? `${txn.entity_name || 'This account\'s owner'} only sends wires for the others. Say whose money this line is; a line with a deal starts at the deal's entity and can be changed.`
                    : 'Whose money this line is. Normally the account\'s owner. Pick another Helena entity when the money is theirs (a payer wired to the wrong account) and the entry posts on both entities\' books, with an intercompany balance between them — see the preview below.'}>
                  Whose money
                </div>
                <div style={colHeaderStyle}>Description</div>
                <div />
              </div>

              {allocs.map((alloc, idx) => (
                <div key={idx} style={{
                  display: 'grid', gridTemplateColumns: allocGrid, gap: 6,
                  padding: '4px 0', borderBottom: `1px solid ${C.border}`, alignItems: 'center',
                }}>
                  <MF value={alloc.amount}
                    onChange={v => updateAlloc(idx, 'amount', v)} placeholder="0.00"
                    style={{ ...inputStyle, textAlign: 'right', fontFamily: theme.fontMono }} />
                  <GLSelect value={alloc.debit_account} onChange={e => updateAlloc(idx, 'debit_account', e.target.value)} style={inputStyle} chart={chart} byValue={chartByValue} inUse={accountsInUse} cashAccount={CASH} cashInfo={cashInfo} />
                  <GLSelect value={alloc.credit_account} onChange={e => updateAlloc(idx, 'credit_account', e.target.value)} style={inputStyle} chart={chart} byValue={chartByValue} inUse={accountsInUse} cashAccount={CASH} cashInfo={cashInfo} />
                  <select value={alloc.deal_id} onChange={e => updateAlloc(idx, 'deal_id', e.target.value)} style={inputStyle}
                    title={alloc.deal_id ? (deals.find(d => String(d.id) === String(alloc.deal_id))?.deal_name || '') : 'Which deal this line belongs to'}>
                    <option value="">— No Deal —</option>
                    {deals.map(d => <option key={d.id} value={d.id}>{d.deal_name}</option>)}
                  </select>
                  {(() => {
                    // A deal PROPOSES its entity on a conduit row; it does not
                    // decide. Brian, 14 September 2026, on Setterwalls
                    // Advokatbyrå's 10,000 for the Klaria Pharma bridge loan:
                    // *"I need to be able to book this to HPI even tho its
                    // tagged to a deal. The deal never went thru and all of
                    // these due diligence costs are effectively a mgmt cost.
                    // Unfair to stick investors with fees and stuff for a deal
                    // that never worked out."* The select used to be locked to
                    // the deal's entity AND to show it, while the line still
                    // carried whatever entity had been chosen — so the screen
                    // read HGIO over a preview that posted to HPI. The lie was
                    // the bug: the server has always let an explicit pick beat
                    // the deal (conduit_entity_for). What is shown here is now
                    // exactly what that resolves — a pick that is not the
                    // conduit, else the deal's entity, else the account owner.
                    const dealEntity = isConduit && alloc.deal_id
                      ? deals.find(d => String(d.id) === String(alloc.deal_id))?.entity : null;
                    const owner = String(txn.entity || '');
                    const picked = alloc.entity_id ? String(alloc.entity_id) : '';
                    // The conduit's own id is what the screen fills in by
                    // default, so it reads as "nothing chosen", not as a
                    // choice — which is why its option is closed off below
                    // rather than left to snap back.
                    const override = picked && picked !== owner ? picked : '';
                    const shown = override || (dealEntity ? String(dealEntity) : (picked || owner));
                    const differs = shown && shown !== owner;
                    const nameOf = (id) => {
                      const en = (entities || []).find(e => String(e.id) === String(id));
                      return en ? (en.short_name || en.name) : '';
                    };
                    // Closed off only when the deal actually points somewhere
                    // else: a deal that IS the account's owner leaves its own
                    // entity pickable, or the select would refuse its value.
                    const ownerLockedOut = !!dealEntity && String(dealEntity) !== owner;
                    return (
                      <select value={shown}
                        onChange={e => updateAlloc(idx, 'entity_id', e.target.value)}
                        style={{ ...inputStyle, ...(differs ? { borderColor: theme.accent, color: theme.accent, fontWeight: 600 } : {}) }}
                        title={dealEntity && override
                                 ? `Booked to ${nameOf(shown)}, not the deal's entity (${nameOf(dealEntity)}) — a cost the deal never bore.`
                                 : dealEntity
                                   ? "Proposed by the deal: a deal's costs and proceeds are normally its entity's. Change it when the cost is not the deal's — a deal that never closed is the management company's expense, not the investors'."
                                   : differs ? 'This line is written to another entity\'s books than the account\'s owner.'
                                             : 'Whose books this line is written to.'}>
                        {(entities || []).map(en => (
                          <option key={en.id} value={en.id} disabled={ownerLockedOut && String(en.id) === owner}>
                            {(en.short_name || en.name) + (ownerLockedOut && String(en.id) === owner ? ' — only sends the wire' : '')}
                          </option>
                        ))}
                      </select>
                    );
                  })()}
                  <input type="text" value={alloc.description} onChange={e => updateAlloc(idx, 'description', e.target.value)}
                    placeholder="Notes…" style={inputStyle} />
                  <button onClick={() => removeLine(idx)} disabled={allocs.length <= 1}
                    style={{ background: 'none', border: 'none', color: allocs.length <= 1 ? C.border : C.red,
                             cursor: allocs.length <= 1 ? 'default' : 'pointer', fontSize: 16 }} title="Remove line">✕</button>
                </div>
              ))}

              <button onClick={addLine} style={{
                marginTop: 8, padding: '6px 14px', borderRadius: 6, border: `1px dashed ${C.border}`,
                background: 'transparent', color: C.accent, fontSize: 12, fontWeight: 600, cursor: 'pointer', width: '100%',
              }}>+ Add Allocation Line</button>
            </>
          )}
        </div>

        {/* Posted ledger entries (read-only, only when posted) */}
        {isPosted && ledgerEntries.length > 0 && (
          <PostedLedgerEntries entries={ledgerEntries} entities={entities || []} deals={deals || []}
            currency={basis.currency} formatAmount={fmtCcy}
            accountHint={account => describeCash(account, cashInfo)} />
        )}
        {/* Notes */}
        <div style={{ marginBottom: 20 }}>
          <label style={{ fontSize: 12, color: C.muted, display: 'block', marginBottom: 4 }}>Review Notes</label>
          {isPosted ? (
            <div style={{
              padding: 8, background: C.inputBg, borderRadius: 6, border: `1px solid ${C.border}`,
              fontSize: 12, color: notes ? C.text : C.muted, fontStyle: notes ? 'normal' : 'italic',
              minHeight: 28,
            }}>{notes || '— no notes —'}</div>
          ) : (
            <textarea value={notes} onChange={e => setNotes(e.target.value)}
              placeholder="Add notes about this transaction…" rows={2}
              style={{ ...inputStyle, width: '100%', resize: 'vertical', fontFamily: 'inherit' }} />
          )}
        </div>

        {/* Ledger entry preview: the server's own answer to "what would post",
            grouped by whose books each leg lands on. */}
        {!isPosted && previewLegs && previewLegs.length > 0 && (() => {
          const byEntity = [];
          previewLegs.forEach(l => {
            let g = byEntity.find(x => x.entity === (l.entity || ''));
            if (!g) { g = { entity: l.entity || '', legs: [] }; byEntity.push(g); }
            g.legs.push(l);
          });
          const twoBooks = byEntity.length > 1;
          const wrap = { ...tdStyle, padding: '4px 8px', whiteSpace: 'normal', wordBreak: 'break-word', verticalAlign: 'top' };
          const acct = (l) => (
            <>
              <div style={{ fontWeight: 600 }}>{l.account}</div>
              {describeCash(l.account, cashInfo) && (
                <div style={{ color: C.muted, fontSize: 11, fontWeight: 500 }}>{describeCash(l.account, cashInfo)}</div>
              )}
              {l.role === 'intercompany' && (
                <div style={{ color: C.muted, fontSize: 11, fontWeight: 500 }}>
                  intercompany — {l.side === 'Debit' ? 'they owe us' : 'we owe them'} after this
                </div>
              )}
            </>
          );
          return (
            <div style={{
              padding: 14, background: C.inputBg, borderRadius: 8, marginBottom: 20,
              border: `1px solid ${C.border}`, fontSize: 12,
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
                <div style={{ fontWeight: 600, color: C.muted, textTransform: 'uppercase', fontSize: 11 }}>
                  Ledger Entry Preview
                </div>
                {twoBooks && (
                  <div style={{ fontSize: 11, color: theme.info }}>
                    Two entities' books. The money moved through one entity's account but belongs to another, so each
                    side shows its own half and an intercompany balance between them; across the group they net to zero.
                  </div>
                )}
              </div>
              {byEntity.map(g => {
                const dr = g.legs.filter(l => l.side === 'Debit').reduce((t, l) => t + (parseFloat(l.amount) || 0), 0);
                const cr = g.legs.filter(l => l.side === 'Credit').reduce((t, l) => t + (parseFloat(l.amount) || 0), 0);
                return (
                  <div key={g.entity} style={{ marginBottom: twoBooks ? 12 : 0 }}>
                    {twoBooks && (
                      <div style={{ fontWeight: 700, fontSize: 12, color: C.text, margin: '4px 0 4px' }}>
                        {g.entity || 'No entity'}'s books
                        <span style={{ fontWeight: 500, color: Math.abs(dr - cr) < 0.01 ? C.green : C.red, marginLeft: 8, fontSize: 11 }}>
                          {Math.abs(dr - cr) < 0.01 ? 'balanced' : `out by ${money(dr - cr)}`}
                        </span>
                      </div>
                    )}
                    <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'auto' }}>
                      <thead>
                        <tr>
                          <th style={{ ...thStyle, padding: '4px 8px', fontSize: 10 }}>Account</th>
                          <th style={{ ...thStyle, padding: '4px 8px', fontSize: 10, textAlign: 'right' }}>Debit</th>
                          <th style={{ ...thStyle, padding: '4px 8px', fontSize: 10, textAlign: 'right' }}>Credit</th>
                          {!twoBooks && <th style={{ ...thStyle, padding: '4px 8px', fontSize: 10 }}>Books</th>}
                          <th style={{ ...thStyle, padding: '4px 8px', fontSize: 10 }}>Deal</th>
                          <th style={{ ...thStyle, padding: '4px 8px', fontSize: 10 }}>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.legs.map((l, i) => (
                          <tr key={i}>
                            <td style={{ ...wrap, paddingLeft: l.side === 'Credit' ? 24 : 8 }}>{acct(l)}</td>
                            <td style={{ ...wrap, textAlign: 'right', color: C.green, fontFamily: theme.fontMono, whiteSpace: 'nowrap' }}>
                              {l.side === 'Debit' ? money(l.amount) : ''}
                            </td>
                            <td style={{ ...wrap, textAlign: 'right', color: C.red, fontFamily: theme.fontMono, whiteSpace: 'nowrap' }}>
                              {l.side === 'Credit' ? money(l.amount) : ''}
                            </td>
                            {!twoBooks && <td style={{ ...wrap, fontSize: 12 }}>{l.entity || '—'}</td>}
                            <td style={{ ...wrap, color: C.purple, fontSize: 12 }}>{l.deal_name || '—'}</td>
                            <td style={{ ...wrap, color: C.muted, fontSize: 12 }}>{l.description || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })}
            </div>
          );
        })()}

        {/* Action buttons */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', alignItems: 'center' }}>
          <button onClick={onClose} style={btnStyle(C.muted)}>Close</button>
          {!isPosted && (
            <>
              {untagged && (
                <span style={{ fontSize: 11, color: C.red, fontWeight: 600, marginRight: 4 }}>
                  Set the dollar amount above before allocating this {rowCcy} payment.
                </span>
              )}
              <button onClick={handleSave} disabled={saving || !allValid || !isBalanced}
                title={untagged ? `This ${rowCcy} payment has no dollar amount recorded.` : ''}
                style={{ ...btnStyle(C.accent), opacity: saving || !allValid || !isBalanced ? 0.5 : 1 }}>
                {saving ? 'Saving…' : 'Save Allocations'}
              </button>
              <button onClick={handlePost} disabled={posting || !allValid || !isBalanced || isUnsettled}
                title={isUnsettled ? `This payment is ${rowStatus}; only a settled payment can be posted.` : ''}
                style={{ ...btnStyle(C.green), opacity: posting || !allValid || !isBalanced || isUnsettled ? 0.5 : 1 }}>
                {posting ? 'Posting…' : 'Save & Post to GL'}
              </button>
            </>
          )}
          {isPosted && (
            <button onClick={handleUnpost} disabled={unposting}
              style={{ ...btnStyle(C.red), opacity: unposting ? 0.5 : 1 }}
              title="Reverse this posting (creates reversing entry dated today)">
              {unposting ? 'Unposting…' : '↶ Unpost'}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}


/* ═══════════════════════ SUB-COMPONENTS ═══════════════════════ */
/* Thumbnails of the pictures on a turn, each removable while it is still
   waiting to be sent. Click one to open it full size. */
function PictureStrip({ pictures, onRemove }) {
  return (
    <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '6px 0 8px' }}>
      {pictures.map((pic, i) => (
        <div key={i} style={{ position: 'relative', border: `1px solid ${C.border}`, borderRadius: 6,
                              background: C.card, padding: 3, maxWidth: 180 }}>
          <a href={pic.preview} target="_blank" rel="noreferrer" title={`${pic.name} — ${pic.width}×${pic.height}`}>
            <img src={pic.preview} alt={pic.name} style={{ display: 'block', height: 56, maxWidth: 172, objectFit: 'contain' }} />
          </a>
          <div style={{ fontSize: 10, color: C.muted, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                        maxWidth: 172, marginTop: 2 }}>{pic.name}</div>
          {onRemove && (
            <button onClick={() => onRemove(i)} title="Remove this picture"
              style={{ position: 'absolute', top: -7, right: -7, width: 18, height: 18, borderRadius: 9,
                       border: `1px solid ${C.border}`, background: C.card, color: C.muted, cursor: 'pointer',
                       fontSize: 11, lineHeight: '14px', padding: 0 }}>×</button>
          )}
        </div>
      ))}
    </div>
  );
}

function InfoField({ label, value, color, span }) {
  return (
    <div style={{ gridColumn: span ? `span ${span}` : undefined }}>
      <div style={{ fontSize: 10, color: C.muted, marginBottom: 2, textTransform: 'uppercase', letterSpacing: '0.3px' }}>{label}</div>
      <div style={{ fontSize: 13, fontWeight: 600, color: color || C.text, wordBreak: 'break-word' }}>{value}</div>
    </div>
  );
}


/* ═══════════════════════ STYLES ═══════════════════════
   Table, input, button and modal styles, all from components/theme.js so the
   page reads as the same product as Ledger and Deals. thStyle and tdStyle
   are the DataTable's own numbers. */
const thStyle = {
  textAlign: 'left', padding: '9px 14px', borderBottom: `2px solid ${theme.border}`,
  background: theme.tableHeaderBg, color: theme.textMuted, fontWeight: 600, fontSize: 11,
  textTransform: 'uppercase', letterSpacing: '0.04em', whiteSpace: 'nowrap', fontFamily: theme.fontBody,
};
const tdStyle = { padding: '10px 14px', borderBottom: `1px solid ${theme.borderLight}`, whiteSpace: 'nowrap', color: theme.text };

/* The Actions column is pinned to the right edge so it stays reachable when
   the table is wider than the screen — it used to scroll off and be cut in
   half. The cell needs an opaque background of its own, or the columns
   scrolling underneath show through it; rows set a solid one and the cell
   inherits it. */
const stickyCell = {
  position: 'sticky', right: 0,
  boxShadow: `-8px 0 8px -8px rgba(44, 40, 37, 0.18)`,
  borderLeft: `1px solid ${theme.borderLight}`,
};

/* The transactions table fits the page instead of scrolling sideways —
   Brian, 12 October 2026: "i dont want to have to scrool left/right in order
   to see them all". It was 1,605px wide in 982px to 1,342px of room, and now
   fits from a 1280px window up, measured in Chromium against the live rows.
   §0.37's rules: text wraps and a figure never does, and nothing is cut off
   with an ellipsis, because a clipped counterparty is the information going
   missing. Headings wrap too, the direction badge moved under the amount, the
   category under the description, and tighter padding buys the rest. The
   Actions column stays pinned for a window narrower than the table can shrink
   to. */
const FEED_PAD = '8px 6px';
const feedThStyle = { ...thStyle, padding: FEED_PAD, whiteSpace: 'normal', lineHeight: 1.25, verticalAlign: 'bottom' };
const feedTdStyle = { ...tdStyle, padding: FEED_PAD };
const feedWrapTdStyle = { ...feedTdStyle, whiteSpace: 'normal', overflowWrap: 'break-word' };
const feedStickyThStyle = { ...feedThStyle, ...stickyCell, zIndex: 3 };
const feedStickyTdStyle = { ...feedTdStyle, ...stickyCell, zIndex: 2 };
// accentLight laid over a white card, as one opaque colour.
const SELECTED_ROW_BG = '#FBF5EC';
// infoBg laid over a white card, as one opaque colour.
const TRANSFER_ROW_BG = '#F3F5F9';

const emptyStyle = { padding: 48, textAlign: 'center', color: theme.textMuted, fontSize: 13 };

/* A status pill in a semantic colour — the shared Badge, for places that
   compose the style rather than render the component. */
const pill = (color) => {
  const [bg, border] = ({
    [theme.success]: [theme.successBg, theme.successBorder],
    [theme.danger]: [theme.dangerBg, theme.dangerBorder],
    [theme.warning]: [theme.warningBg, theme.warningBorder],
    [theme.info]: [theme.infoBg, theme.infoBorder],
    [theme.accent]: [theme.accentLight, theme.accentBorder],
  })[color] || ['rgba(138,128,120,0.08)', 'rgba(138,128,120,0.18)'];
  return {
    display: 'inline-flex', alignItems: 'center', padding: '2px 8px', borderRadius: theme.radiusFull,
    fontSize: 11, fontWeight: 600, fontFamily: theme.fontBody, lineHeight: '16px', whiteSpace: 'nowrap',
    background: bg, color, border: `1px solid ${border}`,
  };
};

/* Buttons in the theme's shapes. A muted colour asks for the secondary
   (bordered, white) button rather than a grey slab. */
const btnStyle = (bg) => {
  const base = {
    padding: '8px 16px', borderRadius: theme.radius, cursor: 'pointer', fontSize: 13,
    fontWeight: 600, fontFamily: theme.fontBody, lineHeight: 1.2, transition: theme.transitionFast,
  };
  if (bg === theme.textMuted || bg === theme.textSecondary) {
    return { ...base, background: theme.bgCard, color: theme.text, border: `1.5px solid ${theme.border}` };
  }
  return { ...base, background: bg, color: theme.textOnAccent, border: 'none' };
};
const smallBtnStyle = (bg) => ({ ...btnStyle(bg), padding: '5px 12px', fontSize: 12 });

const colHeaderStyle = { fontSize: 10, color: theme.textMuted, textTransform: 'uppercase', letterSpacing: '0.04em', fontWeight: 600, fontFamily: theme.fontBody };
const inputStyle = {
  padding: '6px 10px', borderRadius: theme.radius, border: `1px solid ${theme.border}`,
  background: theme.bgInput, color: theme.text, fontSize: 13, fontFamily: theme.fontBody,
  width: '100%', boxSizing: 'border-box',
};
const filterControlStyle = {
  padding: '7px 12px', borderRadius: theme.radius, border: `1px solid ${theme.border}`,
  background: theme.bgInput, color: theme.text, fontSize: 13, fontFamily: theme.fontBody, cursor: 'pointer',
};
const pagerBtnStyle = (disabled) => ({
  padding: '4px 9px', borderRadius: theme.radiusSm, border: `1px solid ${theme.border}`,
  background: disabled ? 'transparent' : theme.bgCard,
  color: disabled ? theme.textDisabled : theme.textSecondary,
  cursor: disabled ? 'not-allowed' : 'pointer', fontSize: 12, fontWeight: 600, fontFamily: theme.fontBody,
});
const overlayStyle = {
  position: 'fixed', inset: 0, background: 'rgba(44, 40, 37, 0.45)', backdropFilter: 'blur(2px)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 999, padding: 20,
};
const modalStyle = {
  background: theme.bgCard, border: `1px solid ${theme.border}`, borderRadius: theme.radiusLg,
  boxShadow: theme.shadowLg, padding: 28, width: 820, maxWidth: '95vw', maxHeight: '92vh',
  overflowY: 'auto', fontFamily: theme.fontBody, color: theme.text,
};
